<template>
  <div class="i1-main c1-instruction-page" @click="c1TopTab = null">

    <!-- TOAST -->
    <transition name="in1-toast-fade">
      <div v-if="i1Toast" class="in1-toast" :style="{ background: i1Toast.color }">{{ i1Toast.msg }}</div>
    </transition>

    <!-- DAILY EXAM MODAL -->
    <transition name="i1-modal-fade">
      <div v-if="i1ExamModal" class="i1-modal-backdrop" @click.self="i1ExamModal = null">
        <div class="i1-modal">
          <div class="i1-modal-head">
            <div class="i1-modal-title">
              <svg viewBox="0 0 24 24"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
              {{ i1ExamModal.title }}
            </div>
            <button class="i1-modal-close" @click="i1ExamModal = null">
              <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          <!-- Exam result screen -->
          <div v-if="i1ExamResult" class="i1-exam-result">
            <div class="i1-exam-result-icon" :class="i1ExamResult.passed ? 'pass' : 'fail'">
              <svg v-if="i1ExamResult.passed" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
              <svg v-else viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </div>
            <div class="i1-exam-result-score">{{ i1ExamResult.correct }}/{{ i1ExamResult.total }}</div>
            <div class="i1-exam-result-pct">{{ i1ExamResult.pct }}% correct</div>
            <div class="i1-exam-result-msg" :class="i1ExamResult.passed ? 'pass' : 'fail'">
              {{ i1ExamResult.passed ? 'PASSED!' : 'FAILED — retry tomorrow' }}
            </div>
            <button class="i1-exam-done-btn" @click="i1ExamModal = null; i1ExamResult = null">Done</button>
          </div>

          <!-- Exam question screen -->
          <div v-else-if="i1ExamModal.questions && i1ExamModal.questions.length" class="i1-exam-body">
            <div class="i1-exam-progress-row">
              <div class="i1-exam-qnum">Question {{ i1ExamQIdx + 1 }} of {{ i1ExamModal.questions.length }}</div>
              <div class="i1-exam-timer" :class="{ urgent: i1ExamTimer <= 10 }">
                <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                {{ i1ExamTimer }}s
              </div>
            </div>
            <div class="i1-exam-progress-bar">
              <div class="i1-exam-progress-fill" :style="{ width: ((i1ExamQIdx / i1ExamModal.questions.length) * 100) + '%' }"></div>
            </div>
            <div class="i1-exam-question">{{ i1ExamModal.questions[i1ExamQIdx].question }}</div>
            <div class="i1-exam-options">
              <div
                v-for="(opt, oi) in i1ExamModal.questions[i1ExamQIdx].options"
                :key="oi"
                class="i1-exam-opt"
                :class="{
                  selected: i1ExamAnswers[i1ExamQIdx] === oi,
                  correct: i1ExamAnswerRevealed && oi === i1ExamModal.questions[i1ExamQIdx].correct,
                  wrong: i1ExamAnswerRevealed && i1ExamAnswers[i1ExamQIdx] === oi && oi !== i1ExamModal.questions[i1ExamQIdx].correct
                }"
                @click="i1SelectAnswer(oi)"
              >
                <span class="i1-exam-opt-letter">{{ ['A','B','C','D'][oi] }}</span>
                {{ opt }}
              </div>
            </div>
            <div class="i1-exam-nav">
              <button
                class="i1-exam-next-btn"
                :disabled="i1ExamAnswers[i1ExamQIdx] === undefined && !i1ExamAnswerRevealed"
                @click="i1ExamNext"
              >
                {{ i1ExamQIdx < i1ExamModal.questions.length - 1 ? 'Next Question' : 'Submit Exam' }}
                <svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>



    <!-- ADD COURSE DRAWER (slides from LEFT, admin only) -->
    <transition name="i1-drawer-left">
      <div v-if="i1AddCourseOpen" class="i1-drawer-wrap i1-drawer-wrap-left" @click.self="i1AddCourseOpen = false">
        <div class="i1-drawer i1-drawer-left">
          <div class="i1-drawer-head">
            <div class="i1-drawer-title-wrap">
              <div class="i1-drawer-title">{{ i1EditingCourse ? 'Edit Course' : 'Add Course' }}</div>
              <div class="i1-drawer-sub">Training module setup</div>
            </div>
            <button class="i1-drawer-close" @click="i1AddCourseOpen = false">
              <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="i1-drawer-body">
            <div class="i1-form-group">
              <label class="i1-form-lbl">Course Title <span class="i1-req">*</span></label>
              <input class="i1-form-input" v-model="i1NewCourse.title" placeholder="e.g. Sales Fundamentals" type="text" />
            </div>
            <div class="i1-form-group">
              <label class="i1-form-lbl">Description</label>
              <textarea class="i1-form-textarea" v-model="i1NewCourse.description" rows="3" placeholder="What will agents learn?"></textarea>
            </div>
            <div class="i1-form-group">
              <label class="i1-form-lbl">Order</label>
              <input class="i1-form-input" v-model.number="i1NewCourse.order" type="number" min="1" placeholder="1" />
            </div>

            <!-- Lessons -->
            <div class="i1-form-section-hdr">
              Lessons
              <button class="i1-form-add-btn" @click="i1AddLesson">+ Add Lesson</button>
            </div>
            <div v-for="(lesson, li) in i1NewCourse.lessons" :key="li" class="i1-form-lesson-block">
              <div class="i1-form-lesson-hdr">
                <span>Lesson {{ li + 1 }}</span>
                <button class="i1-form-remove-btn" @click="i1RemoveLesson(li)">Remove</button>
              </div>
              <input class="i1-form-input" v-model="lesson.title" placeholder="Lesson title" style="margin-bottom:6px" />
              <textarea class="i1-form-textarea" v-model="lesson.content" rows="4" placeholder="Lesson content (HTML supported)"></textarea>
              <input class="i1-form-input" v-model="lesson.video_url" placeholder="Video URL (optional)" style="margin-top:6px" />
            </div>

            <!-- Exam Questions -->
            <div class="i1-form-section-hdr">
              Exam Questions
              <button class="i1-form-add-btn" @click="i1AddQuestion">+ Add Question</button>
            </div>
            <div v-for="(q, qi) in i1NewCourse.questions" :key="'q'+qi" class="i1-form-lesson-block">
              <div class="i1-form-lesson-hdr">
                <span>Question {{ qi + 1 }}</span>
                <button class="i1-form-remove-btn" @click="i1RemoveQuestion(qi)">Remove</button>
              </div>
              <textarea class="i1-form-textarea" v-model="q.question" rows="2" placeholder="Question text" style="margin-bottom:6px"></textarea>
              <div v-for="oi in 4" :key="oi" class="i1-form-option-row">
                <div
                  class="i1-form-correct-dot"
                  :class="{ active: q.correct === (oi - 1) }"
                  @click="q.correct = oi - 1"
                  :title="'Mark as correct answer'"
                ></div>
                <input class="i1-form-input" v-model="q.options[oi - 1]" :placeholder="'Option ' + ['A','B','C','D'][oi-1]" />
              </div>
              <div class="i1-form-correct-hint">Click the dot to mark the correct answer</div>
            </div>

            <div class="i1-form-actions">
              <button class="i1-submit-btn i1-submit-draft" @click="i1AddCourseOpen = false">Cancel</button>
              <button
                class="i1-submit-btn i1-submit-publish"
                :disabled="!i1NewCourse.title || i1FormLoading"
                @click="i1SaveCourse"
              >
                <svg viewBox="0 0 24 24"><path d="M22 2L11 13"/><path d="M22 2L15 22 11 13 2 9l20-7z"/></svg>
                {{ i1EditingCourse ? 'Save Changes' : 'Create Course' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <div class="c1-sticky-top">
    <!-- TOP BAR -->
    <div class="c1-tib" @click="i1SlideOpen = null">
      <div class="c1-tib-side c1-tib-left" style="padding-left:0;gap:8px;">
        <div class="c1-viki-topface"
          :class="{ 'c1-viki-topface--active': i1PanelAtContent && i1PanelActiveContent === 'viki' }"
          @click.stop="i1SelectPanel('viki')"
        >
          <viki-face :mood="c1VikiMood" :speaking="c1VikiSpeaking" :size="44" style="width:72px;height:44px;display:block;overflow:hidden;border-radius:0;"/>
        </div>
        <div class="c1-shift-wrap" @click.stop="c1ShiftMenuOpen = !c1ShiftMenuOpen; c1HamMenuOpen = false">
          <div class="c1-tib-shift-pill" :class="{ open: c1ShiftMenuOpen }">
            ⏱ {{ c1ShiftElapsed }}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:10px;height:10px;margin-left:3px;transition:transform .2s" :style="{ transform: c1ShiftMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)' }"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        </div>
      </div>
      <div class="c1-tib-center">
        <div class="c1-tib-mod-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:14px;height:14px;flex-shrink:0"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
          Instruction
        </div>
      </div>
      <div class="c1-tib-side c1-tib-right">
        <!-- View toggle -->
        <div class="i1-view-toggle">
          <div class="i1-vt-btn" :class="{ on: i1View === 'agent' }" @click.stop="i1View = 'agent'">
            <svg viewBox="0 0 24 24"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
            Agent
          </div>
          <div v-if="i1IsManager" class="i1-vt-btn" :class="{ on: i1View === 'manager' }" @click.stop="i1View = 'manager'; i1LoadManagerData()">
            <svg viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            Manager
          </div>
          <div v-if="i1IsManager" class="i1-vt-btn" :class="{ on: i1View === 'settings' }" @click.stop="i1View = 'settings'; i1OpenProf = null; i1ExamModal = null; i1ExpandedLesson = null; i1AddCourseOpen = false; i1SlideOpen = null">
            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            Settings
          </div>
        </div>
        <div class="c1-ham-wrap" @click.stop>
          <button class="c1-ham-btn" :class="{ open: c1HamMenuOpen }" @click="c1HamMenuOpen = !c1HamMenuOpen; c1ShiftMenuOpen = false">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:18px;height:18px"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          </button>
        </div>
      </div>
    </div>
    <!-- SHIFT DRAWER -->
    <div class="c1-top-drawer c1-shift-drawer" :class="{ open: c1ShiftMenuOpen }" @click.stop>
      <div class="c1-td-inner">
        <div class="c1-td-section-lbl">My Shift</div>
        <div class="c1-td-status-row">
          <div class="c1-td-stat">
            <div class="c1-td-stat-val c1-mono">{{ c1ShiftStartTime ? new Date(c1ShiftStartTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—' }}</div>
            <div class="c1-td-stat-lbl">Started</div>
          </div>
          <div class="c1-td-stat">
            <div class="c1-td-stat-val c1-mono" style="color:var(--c1-dp)">{{ c1ShiftElapsed }}</div>
            <div class="c1-td-stat-lbl">Elapsed</div>
          </div>
          <div class="c1-td-stat">
            <div class="c1-td-stat-val">{{ c1BathroomCount }}×</div>
            <div class="c1-td-stat-lbl">🚽 Bathroom</div>
          </div>
          <div class="c1-td-stat">
            <div class="c1-td-stat-val">{{ c1CoffeeCount }}×</div>
            <div class="c1-td-stat-lbl">☕ Coffee</div>
          </div>
        </div>
        <div class="c1-td-btn-row">
          <button class="c1-td-btn c1-td-btn-shift"   :class="{ active: c1Status === 'shift' }"   @click.stop="c1SetStatus('shift')">▶ On Shift</button>
          <button class="c1-td-btn c1-td-btn-bathroom" :class="{ active: c1Status === 'bathroom' }" @click.stop="c1SetStatus('bathroom')">🚽 Bathroom</button>
          <button class="c1-td-btn c1-td-btn-coffee"  :class="{ active: c1Status === 'coffee' }"  @click.stop="c1SetStatus('coffee')">☕ Coffee</button>
          <button class="c1-td-btn c1-td-btn-break"   :class="{ active: c1Status === 'break' }"   @click.stop="c1SetStatus('break')">⏸ Break</button>
          <button class="c1-td-btn c1-td-btn-lunch"   :class="{ active: c1Status === 'lunch' }"   @click.stop="c1SetStatus('lunch')">🍽 Lunch</button>
          <button class="c1-td-btn c1-td-btn-end"     @click.stop="c1SetStatus('end')">⏹ End Shift</button>
        </div>
      </div>
    </div>
    <!-- HAM NAV DRAWER -->
    <div class="c1-top-drawer c1-ham-drawer" :class="{ open: c1HamMenuOpen }" @click.stop>
      <div class="c1-td-inner c1-td-nav-grid">
        <router-link to="/clients1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:var(--c1-dp)"></span>Clients</router-link>
        <router-link to="/tasks1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:var(--c1-sc)"></span>Tasks</router-link>
        <router-link to="/stats1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:var(--c1-ld)"></span>Statistics</router-link>
        <router-link to="/chat1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:var(--c1-rf)"></span>Chat</router-link>
        <router-link to="/communications1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#3b82f6"></span>Communications</router-link>
        <router-link to="/affiliation1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#8b5cf6"></span>Affiliation</router-link>
        <router-link to="/finance1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#10b981"></span>Finance</router-link>
        <router-link to="/shifts1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#f59e0b"></span>Shifts</router-link>
        <router-link to="/users1" class="c1-td-nav-item" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#06b6d4"></span>Users</router-link>
        <router-link to="/brokers1" class="c1-td-nav-item" :class="{ on: $route.path === '/brokers1' }" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#ec4899"></span>Brokers</router-link>
        <router-link to="/security1" class="c1-td-nav-item" :class="{ on: $route.path === '/security1' }" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#ef4444"></span>Security</router-link>
        <router-link to="/quiz1" class="c1-td-nav-item" :class="{ on: $route.path === '/quiz1' }" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#a78bfa"></span>Quiz</router-link>
        <router-link to="/instruction1" class="c1-td-nav-item" :class="{ on: $route.path === '/instruction1' }" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#64748b"></span>Instructions</router-link>
        <router-link to="/settings1" class="c1-td-nav-item" :class="{ on: $route.path === '/settings1' }" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#94a3b8"></span>Settings</router-link>
        <router-link to="/profile1" class="c1-td-nav-item" :class="{ on: $route.path === '/profile1' }" @click.native="c1HamMenuOpen=false"><span class="c1-td-nav-dot" style="background:#22d3ee"></span>My Profile</router-link>
      </div>
    </div>
    </div><!-- /c1-sticky-top -->

    <!-- TOP-STRIP TABS -->
    <div class="c1-ts-wrap" @click.stop>
      <!-- Status tab -->
      <div class="c1-ts-tab" :class="{on: c1TopTab === 'status'}" @click.stop="c1TopTab = c1TopTab === 'status' ? null : 'status'">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px;flex-shrink:0"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
        <span>Status</span>
        <svg class="c1-ts-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :style="{transform: c1TopTab==='status' ? 'rotate(180deg)' : ''}"><polyline points="6 9 12 15 18 9"/></svg>
      </div>
      <div class="c1-ts-tab-sep"></div>
      <!-- Score tab -->
      <div class="c1-ts-tab" :class="{on: c1TopTab === 'score'}" @click.stop="c1TopTab = c1TopTab === 'score' ? null : 'score'">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px;flex-shrink:0"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
        <span>Score</span>
        <svg class="c1-ts-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :style="{transform: c1TopTab==='score' ? 'rotate(180deg)' : ''}"><polyline points="6 9 12 15 18 9"/></svg>
      </div>
      <div class="c1-ts-tab-sep"></div>
      <!-- Search tab -->
      <div class="c1-ts-tab" :class="{on: c1TopTab === 'search'}" @click.stop="c1TopTab = c1TopTab === 'search' ? null : 'search'">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px;flex-shrink:0"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <span>Search</span>
        <svg class="c1-ts-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :style="{transform: c1TopTab==='search' ? 'rotate(180deg)' : ''}"><polyline points="6 9 12 15 18 9"/></svg>
      </div>
      <div class="c1-ts-tab-sep"></div>
      <!-- Date tab -->
      <div class="c1-ts-tab" :class="{on: c1TopTab === 'date'}" @click.stop="c1TopTab = c1TopTab === 'date' ? null : 'date'">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px;flex-shrink:0"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        <span>Date</span>
        <svg class="c1-ts-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :style="{transform: c1TopTab==='date' ? 'rotate(180deg)' : ''}"><polyline points="6 9 12 15 18 9"/></svg>
      </div>
      <div class="c1-ts-tab-sep"></div>
      <!-- Charts tab -->
      <div class="c1-ts-tab" :class="{on: c1TopTab === 'charts'}" @click.stop="c1TopTab = c1TopTab === 'charts' ? null : 'charts'">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px;flex-shrink:0"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
        <span>Charts</span>
        <svg class="c1-ts-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :style="{transform: c1TopTab==='charts' ? 'rotate(180deg)' : ''}"><polyline points="6 9 12 15 18 9"/></svg>
      </div>
      <!-- Dropdowns -->
      <transition name="c1-ts-drop">
        <div v-if="c1TopTab === 'status'" class="c1-ts-dropdown" @click.stop>
          <div class="i1-fp-group i1-fp-full" style="padding:10px 14px;">
            <div class="i1-fp-opts">
              <div v-for="s in i1StatusFilters" :key="s.id" class="i1-fp-opt" :class="{ on: i1FilterStatus === s.id }" @click="i1FilterStatus = s.id; c1TopTab = null">{{ s.label }}</div>
            </div>
          </div>
        </div>
      </transition>
      <transition name="c1-ts-drop">
        <div v-if="c1TopTab === 'score'" class="c1-ts-dropdown" @click.stop>
          <div style="padding:10px 14px;">
            <div class="i1-fp-opts">
              <div v-for="pct in [0, 50, 70, 80, 90]" :key="pct" class="i1-fp-opt" :class="{ on: i1FilterMinScore === pct }" @click="i1FilterMinScore = pct; c1TopTab = null">{{ pct === 0 ? 'Any' : pct + '%+' }}</div>
            </div>
          </div>
        </div>
      </transition>
      <transition name="c1-ts-drop">
        <div v-if="c1TopTab === 'search'" class="c1-ts-dropdown" @click.stop>
          <div style="padding:10px 14px;">
            <div class="i1-fp-search">
              <svg class="i1-fp-sico" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input class="i1-fp-sinput" v-model="i1RawSearch" placeholder="Search courses, lessons…" type="text" autocomplete="off" />
              <div v-if="i1RawSearch" class="i1-fp-sclear" @click="i1RawSearch = ''">
                <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </div>
            </div>
          </div>
        </div>
      </transition>
      <transition name="c1-ts-drop">
        <div v-if="c1TopTab === 'date'" class="c1-ts-dropdown" @click.stop>
          <div style="padding:10px 14px;">
            <div class="i1-date-range-row">
              <input type="date" class="i1-date-input" v-model="i1FilterDateFrom" />
              <span class="i1-date-sep">→</span>
              <input type="date" class="i1-date-input" v-model="i1FilterDateTo" />
            </div>
          </div>
        </div>
      </transition>
      <transition name="c1-ts-drop">
        <div v-if="c1TopTab === 'charts'" class="c1-ts-dropdown" @click.stop>
          <div class="i1-sl-body i1-charts-body" style="padding:10px 14px;">
            <div class="i1-chart-block">
              <div class="i1-chart-ttl">Completion Rate by Agent</div>
              <div class="i1-cbar-list">
                <div v-for="row in i1CompletionChart" :key="row.name" class="i1-cbar-row">
                  <div class="i1-cbar-name">{{ row.name }}</div>
                  <div class="i1-cbar-track"><div class="i1-cbar-fill" :style="{ width: row.pct + '%', background: '#6366f1' }"></div></div>
                  <div class="i1-cbar-val">{{ row.pct }}%</div>
                </div>
              </div>
            </div>
            <div class="i1-chart-block">
              <div class="i1-chart-ttl">Avg Score by Course</div>
              <div class="i1-cbar-list">
                <div v-for="row in i1ScoreChart" :key="row.name" class="i1-cbar-row">
                  <div class="i1-cbar-name">{{ row.name }}</div>
                  <div class="i1-cbar-track"><div class="i1-cbar-fill" :style="{ width: row.pct + '%', background: row.pct >= 70 ? '#22c55e' : '#ef4444' }"></div></div>
                  <div class="i1-cbar-val" :style="{ color: row.pct >= 70 ? '#22c55e' : '#ef4444' }">{{ row.pct }}%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
      <div class="c1-nm-div"></div>
      <c1-mod-calendar :items="i1Courses" dateField="created_at" itemLabel="instructions" style="flex-shrink:0" />
    </div>

    <!-- PANEL BACKDROP -->
    <transition name="c1-bd-fade">
      <div v-if="i1SlideOpen" class="c1-panel-backdrop" @click.stop="i1SlideOpen = null"></div>
    </transition>

    <!-- UNIFIED PANEL -->
    <div class="c1-panel" :class="{ 'c1-panel--open': i1PanelIsOpen }" @click.capture="i1FilterPanelClick">
      <div class="c1-panel-track" :class="{ 'at-content': i1PanelAtContent }">
        <!-- Screen 0: Nav -->
        <div class="c1-panel-screen c1-panel-screen--nav">

          <!-- User strip -->
          <div class="c1-nav-user-strip">
            <div class="c1-nav-av">
              {{ c1UserInitials }}
              <span class="c1-nav-av-dot" :class="'c1-dst-' + c1Status"></span>
            </div>
            <div class="c1-nav-uinfo">
              <div class="c1-nav-uname">{{ c1UserFullName }}</div>
              <div class="c1-nav-urole">{{ c1RoleLabel }}</div>
            </div>
            <div class="c1-nav-status-wrap" @click.stop="c1StatusMenuOpen = !c1StatusMenuOpen">
              <div class="c1-nav-status-btn" :class="'c1-st-' + c1Status">
                <span class="c1-nav-status-sdot"></span>
                <span class="c1-nav-status-elapsed">{{ c1StatusElapsed }}</span>
              </div>
              <transition name="c1-sfade">
                <div v-if="c1StatusMenuOpen" class="c1-nav-status-menu">
                  <div class="c1-nav-status-opt" @click.stop="c1SetStatus('available')"><span class="c1-nav-status-opt-dot c1-st-dot-available"></span>Available</div>
                  <div class="c1-nav-status-opt" @click.stop="c1SetStatus('bathroom')"><span class="c1-nav-status-opt-dot c1-st-dot-bathroom"></span>Bathroom</div>
                  <div class="c1-nav-status-opt" @click.stop="c1SetStatus('coffee')"><span class="c1-nav-status-opt-dot c1-st-dot-coffee"></span>Coffee break</div>
                  <div class="c1-nav-status-opt" @click.stop="c1SetStatus('shift')"><span class="c1-nav-status-opt-dot c1-st-dot-shift"></span>Start shift</div>
                  <div class="c1-nav-status-opt c1-sto-end" @click.stop="c1SetStatus('end')"><span class="c1-nav-status-opt-dot c1-st-dot-end"></span>End shift</div>
                </div>
              </transition>
            </div>
          </div>

          <!-- Timezone bar -->
          <div class="c1-nav-tz-bar">
            <div class="c1-nav-tz-cell"><span class="c1-nav-tz-flag">🇬🇧</span><span class="c1-nav-tz-time">{{ c1TzUK }}</span></div>
            <div class="c1-nav-tz-sep"></div>
            <div class="c1-nav-tz-cell"><span class="c1-nav-tz-flag">🇮🇱</span><span class="c1-nav-tz-time">{{ c1TzIL }}</span></div>
            <div class="c1-nav-tz-sep"></div>
            <div class="c1-nav-tz-cell"><span class="c1-nav-tz-flag">🏝</span><span class="c1-nav-tz-time">{{ c1TzCB }}</span></div>
          </div>

          <!-- ── NAVIGATE: all pages ── -->
          <div v-if="c1NavMode === 'navigate'" class="c1-sl-body" style="padding:6px 0 12px">
            <div class="c1-psel-sub c1-psel-sub-back" @click.stop="c1NavMode = 'panels'">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:14px;height:14px;flex-shrink:0"><polyline points="15 18 9 12 15 6"/></svg>
              <span class="c1-psel-sub-lbl">{{ c1CurrentPageLabel }}</span>
            <span class="c1-nav-x-btn" @click.stop="i1SlideOpen = null"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:15px;height:15px;display:block"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></span></div>
            <div class="c1-psel-divider"></div>
            <div class="c1-psel-grp-hdr">CRM</div>
            <router-link class="c1-psel-sub" to="/clients1" @click.native="i1SlideOpen = null; sessionStorage.setItem('c1keepNav','1'); sessionStorage.setItem('c1openPanel','filters')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">Clients</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/tasks1" @click.native="i1SlideOpen = null; sessionStorage.setItem('c1keepNav','1'); sessionStorage.setItem('c1openPanel','filters')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">Tasks</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/stats1" @click.native="i1SlideOpen = null; sessionStorage.setItem('c1keepNav','1'); sessionStorage.setItem('c1openPanel','filters')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">Statistics</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/chat1" @click.native="i1SlideOpen = null; sessionStorage.setItem('c1keepNav','1'); sessionStorage.setItem('c1openPanel','filters')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">Chat</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/communications1" @click.native="i1SlideOpen = null; sessionStorage.setItem('c1keepNav','1'); sessionStorage.setItem('c1openPanel','filters')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">Communications</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/affiliation1" @click.native="i1SlideOpen = null; sessionStorage.setItem('c1keepNav','1'); sessionStorage.setItem('c1openPanel','filters')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">Affiliation</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <div class="c1-psel-divider"></div>
            <div class="c1-psel-grp-hdr">Sales</div>
            <router-link class="c1-psel-sub" to="/finance1" @click.native="i1SlideOpen = null; sessionStorage.setItem('c1keepNav','1'); sessionStorage.setItem('c1openPanel','filters')"><span class="c1-psel-sub-dot" style="background:var(--c1-sc)"></span><span class="c1-psel-sub-lbl">Finance</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/shifts1" @click.native="i1SlideOpen = null; sessionStorage.setItem('c1keepNav','1'); sessionStorage.setItem('c1openPanel','filters')"><span class="c1-psel-sub-dot" style="background:var(--c1-sc)"></span><span class="c1-psel-sub-lbl">Shifts</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/games1" @click.native="i1SlideOpen = null; sessionStorage.setItem('c1keepNav','1'); sessionStorage.setItem('c1openPanel','filters')"><span class="c1-psel-sub-dot" style="background:var(--c1-sc)"></span><span class="c1-psel-sub-lbl">Games</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <div class="c1-psel-divider"></div>
            <div class="c1-psel-grp-hdr">Team</div>
            <router-link class="c1-psel-sub" to="/users1" @click.native="i1SlideOpen = null; sessionStorage.setItem('c1keepNav','1'); sessionStorage.setItem('c1openPanel','filters')"><span class="c1-psel-sub-dot" style="background:var(--c1-rf)"></span><span class="c1-psel-sub-lbl">Users</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/brokers1" @click.native="i1SlideOpen = null; sessionStorage.setItem('c1keepNav','1'); sessionStorage.setItem('c1openPanel','filters')"><span class="c1-psel-sub-dot" style="background:var(--c1-rf)"></span><span class="c1-psel-sub-lbl">Brokers</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/security1" @click.native="i1SlideOpen = null; sessionStorage.setItem('c1keepNav','1'); sessionStorage.setItem('c1openPanel','filters')"><span class="c1-psel-sub-dot" style="background:var(--c1-rf)"></span><span class="c1-psel-sub-lbl">Security</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <div class="c1-psel-divider"></div>
            <div class="c1-psel-grp-hdr">Admin</div>
            <router-link class="c1-psel-sub" to="/quiz1" @click.native="i1SlideOpen = null; sessionStorage.setItem('c1keepNav','1'); sessionStorage.setItem('c1openPanel','filters')"><span class="c1-psel-sub-dot" style="background:#7c6f9f"></span><span class="c1-psel-sub-lbl">Quiz</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/instruction1" @click.native="i1SlideOpen = null; sessionStorage.setItem('c1keepNav','1'); sessionStorage.setItem('c1openPanel','filters')"><span class="c1-psel-sub-dot" style="background:#7c6f9f"></span><span class="c1-psel-sub-lbl">Instructions</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/settings1" @click.native="i1SlideOpen = null; sessionStorage.setItem('c1keepNav','1'); sessionStorage.setItem('c1openPanel','filters')"><span class="c1-psel-sub-dot" style="background:#7c6f9f"></span><span class="c1-psel-sub-lbl">Settings</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
            <router-link class="c1-psel-sub" to="/profile1" @click.native="i1SlideOpen = null; sessionStorage.setItem('c1keepNav','1'); sessionStorage.setItem('c1openPanel','filters')"><span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span><span class="c1-psel-sub-lbl">My Profile</span><svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg></router-link>
          </div>

          <!-- Panels mode -->
          <div v-else class="c1-sl-body" style="padding:6px 0 16px">
            <!-- Current page + navigate -->
            <div class="c1-psel-sub c1-psel-sub-navigate" @click.stop="c1NavMode = 'navigate'" style="display:flex;align-items:center;gap:8px;padding:10px 14px;cursor:pointer;">
              <span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span>
              <span class="c1-psel-sub-lbl" style="font-weight:600;color:var(--c1-dp)">{{ c1CurrentPageLabel }}</span>
              <span class="c1-psel-sub-hint">Go to another page</span>
              <svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
            <div class="c1-psel-divider"></div>

            <!-- FILTERS -->
            <div class="c1-psel-grp-hdr">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
              Filters
            </div>
            <div class="c1-psel-sub" @click.stop="i1SelectPanel('filters')">
              <span class="c1-psel-sub-dot" style="background:var(--c1-dp)"></span>
              <span class="c1-psel-sub-lbl">Status &amp; Search</span>
              <span class="c1-psel-sub-hint">Filter courses</span>
              <svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
            <div class="c1-psel-divider"></div>

            <!-- ANALYTICS -->
            <div class="c1-psel-grp-hdr">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
              Analytics
            </div>
            <div class="c1-psel-sub" @click.stop="i1SelectPanel('charts')">
              <span class="c1-psel-sub-dot" style="background:#6366f1"></span>
              <span class="c1-psel-sub-lbl">Completion Rate</span>
              <span class="c1-psel-sub-hint">By agent</span>
              <svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
            <div class="c1-psel-sub" @click.stop="i1SelectPanel('charts')">
              <span class="c1-psel-sub-dot" style="background:#22c55e"></span>
              <span class="c1-psel-sub-lbl">Avg Score</span>
              <span class="c1-psel-sub-hint">By course</span>
              <svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
            <div class="c1-psel-divider"></div>

            <!-- VIKI AI -->
            <div class="c1-psel-grp-hdr">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
              Viki AI
            </div>
            <div class="c1-psel-sub" @click.stop="i1SelectPanel('viki')">
              <span class="c1-psel-sub-dot" style="background:#e86840"></span>
              <span class="c1-psel-sub-lbl">Messages</span>
              <span class="c1-psel-sub-hint">Notifications &amp; tips</span>
              <svg class="c1-psel-nav-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
          </div><!-- end panels mode -->
        </div>
        <!-- Screen 1: Content -->
        <div class="c1-panel-screen c1-panel-screen--content">
          <!-- Filters -->
          <template v-if="i1PanelActiveContent === 'filters'">
            <div class="i1-panel-hdr-bar">
              <div class="i1-collapse-btn" @click.stop="i1SelectPanel('nav')">
                <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
              </div>
              <span class="i1-panel-hdr-ttl">Filters</span>
            </div>
            <div class="i1-sl-body">
              <!-- Status filter -->
              <div class="i1-fp-group i1-fp-full">
                <div class="i1-fp-lbl">Status</div>
                <div class="i1-fp-opts">
                  <div v-for="s in i1StatusFilters" :key="s.id" class="i1-fp-opt" :class="{ on: i1FilterStatus === s.id }" @click="i1FilterStatus = s.id; i1SlideOpen = null">{{ s.label }}</div>
                </div>
              </div>
              <!-- Score range -->
              <div class="i1-fp-group i1-fp-full">
                <div class="i1-fp-lbl">Min Score</div>
                <div class="i1-fp-opts">
                  <div v-for="pct in [0, 50, 70, 80, 90]" :key="pct" class="i1-fp-opt" :class="{ on: i1FilterMinScore === pct }" @click="i1FilterMinScore = pct">{{ pct === 0 ? 'Any' : pct + '%+' }}</div>
                </div>
              </div>
              <!-- Search -->
              <div class="i1-fp-group i1-fp-full">
                <div class="i1-fp-lbl">Search</div>
                <div class="i1-fp-search">
                  <svg class="i1-fp-sico" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input class="i1-fp-sinput" v-model="i1RawSearch" placeholder="Search courses, lessons…" type="text" autocomplete="off" />
                  <div v-if="i1RawSearch" class="i1-fp-sclear" @click="i1RawSearch = ''">
                    <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </div>
                </div>
              </div>
              <!-- Date range -->
              <div class="i1-fp-group i1-fp-full">
                <div class="i1-fp-lbl">Exam Date Range</div>
                <div class="i1-date-range-row">
                  <input type="date" class="i1-date-input" v-model="i1FilterDateFrom" />
                  <span class="i1-date-sep">→</span>
                  <input type="date" class="i1-date-input" v-model="i1FilterDateTo" />
                </div>
              </div>
            </div>
          </template>
          <!-- Analytics -->
          <template v-if="i1PanelActiveContent === 'charts'">
            <div class="i1-panel-hdr-bar">
              <div class="i1-collapse-btn" @click.stop="i1SelectPanel('nav')">
                <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
              </div>
              <span class="i1-panel-hdr-ttl">Analytics</span>
            </div>
            <div class="i1-sl-body i1-charts-body">
              <!-- Completion rate by agent -->
              <div class="i1-chart-block">
                <div class="i1-chart-ttl">Completion Rate by Agent</div>
                <div class="i1-cbar-list">
                  <div v-for="row in i1CompletionChart" :key="row.name" class="i1-cbar-row">
                    <div class="i1-cbar-name">{{ row.name }}</div>
                    <div class="i1-cbar-track"><div class="i1-cbar-fill" :style="{ width: row.pct + '%', background: '#6366f1' }"></div></div>
                    <div class="i1-cbar-val">{{ row.pct }}%</div>
                  </div>
                </div>
              </div>
              <!-- Avg score by course -->
              <div class="i1-chart-block">
                <div class="i1-chart-ttl">Avg Score by Course</div>
                <div class="i1-cbar-list">
                  <div v-for="row in i1ScoreChart" :key="row.name" class="i1-cbar-row">
                    <div class="i1-cbar-name">{{ row.name }}</div>
                    <div class="i1-cbar-track"><div class="i1-cbar-fill" :style="{ width: row.pct + '%', background: row.pct >= 70 ? '#22c55e' : '#ef4444' }"></div></div>
                    <div class="i1-cbar-val" :style="{ color: row.pct >= 70 ? '#22c55e' : '#ef4444' }">{{ row.pct }}%</div>
                  </div>
                </div>
              </div>
              <!-- Score trend -->
              <div class="i1-chart-block">
                <div class="i1-chart-ttl">Score Trend (Last 7 Exams)</div>
                <div class="i1-sparkline-wrap">
                  <svg class="i1-sparkline" viewBox="0 0 200 60" preserveAspectRatio="none">
                    <polyline :points="i1ScoreTrendPoints" fill="none" stroke="#6366f1" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>
                    <circle v-for="(pt, pi) in i1ScoreTrendDots" :key="pi" :cx="pt.x" :cy="pt.y" r="3" fill="#6366f1"/>
                  </svg>
                </div>
              </div>
            </div>
          </template>
          <!-- Viki -->
          <template v-if="i1PanelActiveContent === 'viki'">
            <div class="i1-panel-hdr-bar">
              <div class="i1-collapse-btn" @click.stop="i1SelectPanel('nav')">
                <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
              </div>
              <span class="i1-panel-hdr-ttl">Viki</span>
            </div>
            <div class="i1-viki-messages">
              <div v-if="!i1VikiMessages.length" class="i1-viki-empty">
                <svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                <span>No notifications</span>
              </div>
              <div v-for="(msg, mi) in i1VikiMessages" :key="mi" class="i1-viki-msg" :class="'i1-viki-' + msg.type">
                <div class="i1-viki-msg-icon">
                  <svg v-if="msg.type === 'exam'" viewBox="0 0 24 24"><path d="M9 11l3 3L22 4"/></svg>
                  <svg v-else-if="msg.type === 'congrats'" viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  <svg v-else viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                </div>
                <div class="i1-viki-msg-body">
                  <div class="i1-viki-msg-text">{{ msg.text }}</div>
                  <div v-if="msg.action" class="i1-viki-msg-action" @click="msg.action.fn(); i1SlideOpen = null">{{ msg.action.label }}</div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>


    <!-- ============================================================ -->
    <!-- AGENT VIEW -->
    <!-- ============================================================ -->
    <div v-if="i1View === 'agent'" class="i1-body">

      <!-- Daily exam banner -->
      <div v-if="i1HasDailyExam" class="i1-exam-banner" @click="i1OpenDailyExam">
        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        <div class="i1-exam-banner-text">
          <strong>Daily exam due!</strong>
          <span>{{ i1DailyExamCourse && i1DailyExamCourse.title }}</span>
        </div>
        <div class="i1-exam-banner-cta">Start Now</div>
      </div>

      <!-- Profession Analogies section -->
      <div class="i1-section-hdr">
        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/></svg>
        Profession Analogies
      </div>
      <div class="i1-analogies-grid">
        <template v-for="prof in i1Professions">
          <div
            :key="prof.id"
            class="i1-analogy-chip"
            :class="{ active: i1OpenProf && i1OpenProf.profId === prof.id }"
            @click="i1ToggleAnalogy(prof)"
          >
            <span class="i1-analogy-chip-icon">{{ prof.icon }}</span>
            <span class="i1-analogy-chip-name">{{ prof.name }}</span>
            <span class="i1-analogy-chip-count">{{ prof.analogies.length }}</span>
          </div>
          <div
            v-if="i1OpenProf && i1OpenProf.profId === prof.id"
            :key="'card-' + prof.id"
            class="i1-analogy-card i1-analogy-card-inline"
          >
            <div class="i1-analogy-card-hdr">
              <div class="i1-analogy-card-badge">{{ i1OpenProf.profession }}</div>
              <button class="i1-analogy-card-close" @click="i1OpenProf = null">
                <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div class="i1-analogy-card-text">{{ i1OpenProf.current }}</div>
            <div class="i1-analogy-card-foot">
              <button class="i1-analogy-refresh-btn" @click="i1RefreshAnalogy">
                <svg viewBox="0 0 24 24"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.95"/></svg>
                Different analogy
              </button>
              <span class="i1-analogy-count">{{ i1OpenProf.idx + 1 }}/{{ i1OpenProf.all.length }}</span>
            </div>
          </div>
        </template>
      </div>

      <!-- Course list -->
      <div class="i1-section-hdr">
        <svg viewBox="0 0 24 24"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
        Training Courses
        <span class="i1-section-count">{{ i1FilteredCourses.length }}</span>
        <button v-if="i1IsAdmin" class="i1-section-add-btn" @click="i1OpenAddCourse">
          <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Course
        </button>
      </div>

      <div v-if="i1Loading" class="i1-loading">
        <div class="i1-spinner"></div>
        <div class="i1-loading-txt">Loading courses…</div>
      </div>

      <div v-else-if="i1FilteredCourses.length === 0" class="i1-empty">
        <div class="i1-empty-icon"><svg viewBox="0 0 24 24"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg></div>
        <div class="i1-empty-msg">No courses found</div>
      </div>

      <div v-else class="i1-course-list">
        <div
          v-for="course in i1FilteredCourses"
          :key="course.id"
          class="i1-course-row"
          :class="{ expanded: i1ExpandedCourse === course.id }"
        >
          <!-- Course header row -->
          <div class="i1-course-head" @click="i1ToggleCourse(course.id)">
            <div class="i1-course-num">{{ course.order || '#' }}</div>
            <div class="i1-course-info">
              <div class="i1-course-title">{{ course.title }}</div>
              <div class="i1-course-progress-row">
                <div class="i1-course-prog-track">
                  <div class="i1-course-prog-fill" :style="{ width: i1CourseProgress(course) + '%' }"></div>
                </div>
                <span class="i1-course-prog-pct">{{ i1CourseProgress(course) }}%</span>
              </div>
            </div>
            <div class="i1-course-status-chip" :class="'status-' + i1CourseStatusSlug(course)">
              {{ i1CourseStatusLabel(course) }}
            </div>
            <div class="i1-course-chv" :class="{ open: i1ExpandedCourse === course.id }">
              <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
          </div>

          <!-- Course expanded body -->
          <div v-if="i1ExpandedCourse === course.id" class="i1-course-expanded">
            <div v-if="course.description" class="i1-course-desc">{{ course.description }}</div>

            <!-- Lessons list -->
            <div class="i1-lessons-list">
              <div
                v-for="(lesson, li) in (i1CourseLessons[course.id] || [])"
                :key="lesson.id"
                class="i1-lesson-acc"
              >
                <!-- Lesson header row -->
                <div
                  class="i1-lesson-row"
                  :class="{
                    read: i1IsLessonRead(course.id, lesson.id),
                    open: i1IsLessonOpen(course.id, lesson.id)
                  }"
                  @click="i1ToggleLesson(course, lesson)"
                >
                  <div class="i1-lesson-status-dot" :class="{ read: i1IsLessonRead(course.id, lesson.id) }">
                    <svg v-if="i1IsLessonRead(course.id, lesson.id)" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                    <span v-else>{{ li + 1 }}</span>
                  </div>
                  <div class="i1-lesson-title">{{ lesson.title }}</div>
                  <svg class="i1-lesson-chv" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
                </div>

                <!-- Inline lesson content accordion -->
                <div v-if="i1IsLessonOpen(course.id, lesson.id)" class="i1-lesson-body">
                  <div class="i1-lesson-body-pos">Lesson {{ li + 1 }} / {{ (i1CourseLessons[course.id] || []).length }}</div>
                  <div v-if="lesson.video_url" class="i1-lesson-video">
                    <iframe :src="lesson.video_url" frameborder="0" allowfullscreen></iframe>
                  </div>
                  <div v-if="lesson.images && lesson.images.length" class="i1-lesson-images">
                    <img v-for="(img, ii) in lesson.images" :key="ii" :src="img" class="i1-lesson-img" />
                  </div>
                  <div class="i1-doc-content" v-html="lesson.content"></div>
                  <div class="i1-lesson-actions">
                    <button
                      class="i1-lesson-read-btn"
                      :class="{ done: i1IsLessonRead(course.id, lesson.id) }"
                      @click.stop="i1MarkLessonRead(course.id, lesson.id)"
                    >
                      <svg v-if="i1IsLessonRead(course.id, lesson.id)" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                      <svg v-else viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 8 12 12 14 14"/></svg>
                      {{ i1IsLessonRead(course.id, lesson.id) ? 'Read ✓' : 'Mark as Read' }}
                    </button>
                    <button
                      v-if="li < (i1CourseLessons[course.id] || []).length - 1"
                      class="i1-lesson-next-btn"
                      @click.stop="i1OpenNextLesson(course, li)"
                    >
                      Next Lesson
                      <svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>
                    </button>
                    <button
                      v-else-if="i1AllLessonsRead(course)"
                      class="i1-lesson-exam-btn"
                      @click.stop="i1StartExam(course)"
                    >
                      <svg viewBox="0 0 24 24"><path d="M9 11l3 3L22 4"/></svg>
                      Take Exam
                    </button>
                  </div>
                </div>
              </div>
              <div v-if="i1LessonsLoading[course.id]" class="i1-lessons-loading">
                <div class="i1-spinner i1-spinner-sm"></div> Loading lessons…
              </div>
            </div>

            <!-- Course actions -->
            <div class="i1-course-actions">
              <button
                v-if="i1CourseStatusSlug(course) === 'not-started'"
                class="i1-course-btn i1-course-btn-start"
                @click.stop="i1StartCourse(course)"
              >
                <svg viewBox="0 0 24 24"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                Start Course
              </button>
              <button
                v-else-if="i1CourseStatusSlug(course) === 'in-progress'"
                class="i1-course-btn i1-course-btn-continue"
                @click.stop="i1ContinueCourse(course)"
              >
                <svg viewBox="0 0 24 24"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                Continue
              </button>
              <button
                v-if="i1CourseStatusSlug(course) === 'exam-due' || (i1AllLessonsRead(course) && i1CourseStatusSlug(course) !== 'completed')"
                class="i1-course-btn i1-course-btn-exam"
                @click.stop="i1StartExam(course)"
              >
                <svg viewBox="0 0 24 24"><path d="M9 11l3 3L22 4"/></svg>
                Take Exam
              </button>
              <div v-if="i1LastScore(course.id) !== null" class="i1-course-last-score">
                Last score: <strong :class="i1LastScore(course.id) >= 70 ? 'pass' : 'fail'">{{ i1LastScore(course.id) }}%</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- MANAGER VIEW -->
    <!-- ============================================================ -->
    <div v-if="i1View === 'manager' && i1IsManager" class="i1-body">

      <!-- Manager sort bar -->
      <div class="i1-mgr-sort-bar">
        <span class="i1-mgr-sort-lbl">Sort:</span>
        <div v-for="s in i1MgrSorts" :key="s.id" class="i1-mgr-sort-btn" :class="{ on: i1MgrSort === s.id }" @click="i1MgrSort = s.id">{{ s.label }}</div>
      </div>

      <!-- Agent detail drawer -->
      <transition name="i1-drawer">
        <div v-if="i1AgentDetail" class="i1-drawer-wrap" @click.self="i1AgentDetail = null">
          <div class="i1-drawer">
            <div class="i1-drawer-head">
              <div class="i1-drawer-back" @click="i1AgentDetail = null">
                <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
              </div>
              <div class="i1-drawer-title-wrap">
                <div class="i1-drawer-title">{{ i1AgentDetail.name }}</div>
                <div class="i1-drawer-sub">Training history</div>
              </div>
              <button class="i1-drawer-close" @click="i1AgentDetail = null">
                <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div class="i1-drawer-body">
              <div class="i1-agent-summary-row">
                <div class="i1-agent-stat-card">
                  <div class="i1-agent-stat-val">{{ i1AgentDetail.courses_completed }}</div>
                  <div class="i1-agent-stat-lbl">Completed</div>
                </div>
                <div class="i1-agent-stat-card">
                  <div class="i1-agent-stat-val" :class="i1AgentDetail.avg_score >= 70 ? 'pass' : 'fail'">{{ i1AgentDetail.avg_score }}%</div>
                  <div class="i1-agent-stat-lbl">Avg Score</div>
                </div>
                <div class="i1-agent-stat-card">
                  <div class="i1-agent-stat-val">{{ i1AgentDetail.last_exam_date || '—' }}</div>
                  <div class="i1-agent-stat-lbl">Last Exam</div>
                </div>
              </div>
              <div class="i1-agent-history-ttl">Course History</div>
              <div v-for="entry in (i1AgentDetail.history || [])" :key="entry.course_id" class="i1-agent-history-row">
                <div class="i1-agent-history-name">{{ entry.course_title }}</div>
                <div class="i1-agent-history-score" :class="entry.score >= 70 ? 'pass' : (entry.score === null ? 'none' : 'fail')">
                  {{ entry.score !== null ? entry.score + '%' : 'Not taken' }}
                </div>
                <div class="i1-agent-history-status" :class="'status-' + entry.status">{{ entry.status }}</div>
                <div class="i1-agent-history-date">{{ entry.exam_date || '—' }}</div>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <!-- Manager loading -->
      <div v-if="i1MgrLoading" class="i1-loading">
        <div class="i1-spinner"></div>
        <div class="i1-loading-txt">Loading agent data…</div>
      </div>

      <div v-else class="i1-mgr-table-wrap">
        <table class="i1-mgr-table">
          <thead>
            <tr>
              <th>Agent</th>
              <th>Courses Done</th>
              <th>Avg Score</th>
              <th>Last Exam</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="agent in i1SortedAgents"
              :key="agent.id"
              class="i1-mgr-row"
              :class="{ 'i1-mgr-row-alert': i1AgentInactive(agent) }"
              @click="i1OpenAgentDetail(agent)"
            >
              <td>
                <div class="i1-mgr-agent-cell">
                  <div class="i1-mgr-av" :style="{ background: i1AuthorColor(agent.name) }">{{ i1AuthorInitials(agent.name) }}</div>
                  <span>{{ agent.name }}</span>
                  <span v-if="i1AgentInactive(agent)" class="i1-mgr-inactive-badge">Inactive 7d</span>
                </div>
              </td>
              <td>{{ agent.courses_completed }}/{{ i1Courses.length }}</td>
              <td>
                <span :class="agent.avg_score >= 70 ? 'i1-score-pass' : 'i1-score-fail'">
                  {{ agent.avg_score !== null ? agent.avg_score + '%' : '—' }}
                </span>
              </td>
              <td>{{ agent.last_exam_date || '—' }}</td>
              <td>
                <span class="i1-status-pill" :class="'status-' + (agent.status || 'none')">{{ agent.status || 'no activity' }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- SETTINGS VIEW -->
    <!-- ============================================================ -->
    <div v-if="i1View === 'settings' && i1IsManager" class="i1-body i1-settings-wrap">

      <div class="i1-set-section">
        <div class="i1-set-section-title">
          <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          Daily Exam Schedule
        </div>
        <div class="i1-set-grid">
          <div class="i1-set-row">
            <div class="i1-set-lbl">
              <div class="i1-set-lbl-name">Daily exam frequency</div>
              <div class="i1-set-lbl-hint">How many exam questions per day per agent</div>
            </div>
            <div class="i1-set-ctrl">
              <div class="i1-set-num-wrap">
                <input type="number" class="i1-set-num" v-model.number="i1Settings.dailyExamQuestions" min="1" max="30" />
                <span class="i1-set-unit">Q</span>
              </div>
            </div>
          </div>
          <div class="i1-set-row">
            <div class="i1-set-lbl">
              <div class="i1-set-lbl-name">Time allowed per question</div>
              <div class="i1-set-lbl-hint">Seconds before auto-advancing</div>
            </div>
            <div class="i1-set-ctrl">
              <div class="i1-set-num-wrap">
                <input type="number" class="i1-set-num" v-model.number="i1Settings.examTimerSeconds" min="10" max="120" />
                <span class="i1-set-unit">sec</span>
              </div>
            </div>
          </div>
          <div class="i1-set-row">
            <div class="i1-set-lbl">
              <div class="i1-set-lbl-name">Mandatory daily exam</div>
              <div class="i1-set-lbl-hint">Agents must complete the daily exam before starting work</div>
            </div>
            <div class="i1-set-ctrl">
              <div class="i1-set-toggle" :class="{ on: i1Settings.mandatoryExam }" @click="i1Settings.mandatoryExam = !i1Settings.mandatoryExam">
                <div class="i1-set-toggle-knob"></div>
              </div>
            </div>
          </div>
          <div class="i1-set-row">
            <div class="i1-set-lbl">
              <div class="i1-set-lbl-name">Completion required for course access</div>
              <div class="i1-set-lbl-hint">Agents must pass previous course exam to unlock next</div>
            </div>
            <div class="i1-set-ctrl">
              <div class="i1-set-toggle" :class="{ on: i1Settings.linearProgress }" @click="i1Settings.linearProgress = !i1Settings.linearProgress">
                <div class="i1-set-toggle-knob"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="i1-set-section">
        <div class="i1-set-section-title">
          <svg viewBox="0 0 24 24"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>
          Reminders & Notifications
        </div>
        <div class="i1-set-grid">
          <div class="i1-set-row">
            <div class="i1-set-lbl">
              <div class="i1-set-lbl-name">Notify manager when agent falls behind</div>
              <div class="i1-set-lbl-hint">Alert when an agent hasn't completed a lesson in 7 days</div>
            </div>
            <div class="i1-set-ctrl">
              <div class="i1-set-toggle" :class="{ on: i1Settings.notifyBehind }" @click="i1Settings.notifyBehind = !i1Settings.notifyBehind">
                <div class="i1-set-toggle-knob"></div>
              </div>
            </div>
          </div>
          <div class="i1-set-row">
            <div class="i1-set-lbl">
              <div class="i1-set-lbl-name">Send weekly progress digest</div>
              <div class="i1-set-lbl-hint">Email manager each Monday with full team progress report</div>
            </div>
            <div class="i1-set-ctrl">
              <div class="i1-set-toggle" :class="{ on: i1Settings.weeklyDigest }" @click="i1Settings.weeklyDigest = !i1Settings.weeklyDigest">
                <div class="i1-set-toggle-knob"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="i1-set-section">
        <div class="i1-set-section-title">
          <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
          Agent-Visible Instruction Text
        </div>
        <div class="i1-set-grid">
          <div class="i1-set-row i1-set-row-col">
            <div class="i1-set-lbl">
              <div class="i1-set-lbl-name">Welcome message shown to all agents</div>
              <div class="i1-set-lbl-hint">Displayed at the top of the Training page for agents</div>
            </div>
            <textarea class="i1-set-textarea" v-model="i1Settings.welcomeText" rows="4" placeholder="e.g. Complete all courses before your first client call. Your progress is tracked and reported to your team leader."></textarea>
          </div>
          <div class="i1-set-row i1-set-row-col">
            <div class="i1-set-lbl">
              <div class="i1-set-lbl-name">Pre-exam instruction</div>
              <div class="i1-set-lbl-hint">Shown to agents on the daily exam start screen</div>
            </div>
            <textarea class="i1-set-textarea" v-model="i1Settings.examInstruction" rows="3" placeholder="e.g. You have 30 seconds per question. Your score is logged. Good luck."></textarea>
          </div>
        </div>
      </div>

      <div class="i1-set-actions">
        <button class="i1-btn-save" :class="{ loading: i1SettingsSaving }" @click="i1SaveSettings">
          <svg v-if="!i1SettingsSaving" viewBox="0 0 24 24"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
          <div v-else class="i1-spin-sm"></div>
          Save settings
        </button>
      </div>
    </div>


  </div>
</template>

<script>
import VikiFace from "../../clients1.feature/VikiFace.vue";
import C1ModCalendar from '../../../components/C1ModCalendar.vue';

const API = '/api';
const tok = () => localStorage.getItem('user-token');
const auth = () => ({ Authorization: 'Bearer ' + tok(), 'Content-Type': 'application/json' });

// ── PROFESSION ANALOGIES DATA ─────────────────────────────────────────────────
const PROFESSIONS = [
  { id: 'it', name: 'IT / Tech', icon: '💻', analogies: [
    'Think of investing like upgrading your server — upfront cost, long-term speed gains.',
    'Your portfolio is like a RAID array — diversification protects against single points of failure.',
    'Market volatility is like a DDoS attack — temporary disruption, systems recover.',
    'Compound interest is like recursive functions — small inputs, exponentially growing outputs.'
  ]},
  { id: 'doctor', name: 'Doctor / Medical', icon: '🩺', analogies: [
    'Investing is preventative medicine for your finances — act now, avoid crisis later.',
    'A diversified portfolio is like a complete treatment plan — multiple approaches working together.',
    'Market dips are like a temporary fever — the body (market) is fighting, recovery follows.',
    'A financial advisor is your specialist referral — expert guidance for complex decisions.'
  ]},
  { id: 'lawyer', name: 'Lawyer / Legal', icon: '⚖️', analogies: [
    'Diversification is your legal precedent — proven strategy that holds up over time.',
    'Investment contracts work like binding agreements — clarity upfront protects both sides.',
    'Risk management is due diligence — you research before committing.',
    'Compounding is like billable hours accumulating — steady, consistent, powerful.'
  ]},
  { id: 'teacher', name: 'Teacher / Education', icon: '📚', analogies: [
    'Investing is like curriculum planning — structured approach yields measurable results.',
    'Dollar-cost averaging is like grading on a curve — smooths out the extremes.',
    'Long-term investing is like education itself — the payoff comes years later, but it is real.',
    'Market research is lesson prep — the more you know going in, the better the outcome.'
  ]},
  { id: 'engineer', name: 'Engineer', icon: '🔧', analogies: [
    'A portfolio is a system architecture — each component has a role, redundancy matters.',
    'Risk tolerance is your safety margin — engineers always build in a buffer.',
    'Stop-loss orders are like circuit breakers — automatic protection when things go wrong.',
    'Backtesting strategies is like running simulations — validate before you deploy to production.'
  ]},
  { id: 'chef', name: 'Chef / Restaurant', icon: '🍳', analogies: [
    'Portfolio balance is like a recipe — too much of one ingredient ruins the dish.',
    'Market timing is like cooking temperature — get it right and results are perfect.',
    'Diversification is your menu — no single dish makes or breaks the night.',
    'Patience in investing is like slow cooking — low heat, long time, best results.'
  ]},
  { id: 'athlete', name: 'Athlete / Sports', icon: '🏅', analogies: [
    'Consistent investing is like daily training — small sessions compound into championship results.',
    'Market drawdowns are like losing seasons — champions stay the course and come back stronger.',
    'Asset allocation is your team lineup — different players (assets) for different game situations.',
    'Rebalancing is like adjusting your training plan — periodic recalibration keeps you on track.'
  ]},
  { id: 'architect', name: 'Architect', icon: '🏛️', analogies: [
    'Your financial plan is a blueprint — every decision builds on the foundation.',
    'Diversification is structural redundancy — multiple load-bearing walls prevent collapse.',
    'Long-term investing is like designing for decades, not just occupancy today.',
    'Compounding is like compound materials — layers of strength built over time.'
  ]},
  { id: 'accountant', name: 'Accountant / Finance', icon: '📊', analogies: [
    'Investing is the income statement you build for your future self.',
    'Diversification is your hedge against off-balance items — nothing too concentrated.',
    'Market cycles are quarterly earnings seasons — up and down, but trend matters.',
    'Tax-efficient investing is like clean bookkeeping — fewer surprises come year-end.'
  ]},
  { id: 'farmer', name: 'Farmer / Agriculture', icon: '🌾', analogies: [
    'Planting seeds early is compound interest — small investment today, harvest for years.',
    'Diversified crops are diversified assets — one bad season does not ruin the farm.',
    'Market patience is like waiting for harvest season — you cannot rush what takes time.',
    'Reinvesting dividends is plowing profits back into next season\'s crop.'
  ]},
  { id: 'pilot', name: 'Pilot / Aviation', icon: '✈️', analogies: [
    'A financial plan is your flight plan — destination set, course corrections expected.',
    'Risk management is your pre-flight checklist — every item matters.',
    'Turbulence is market volatility — experienced pilots (investors) stay calm and continue.',
    'Autopilot is dollar-cost averaging — systematic, consistent, removing emotion.'
  ]},
  { id: 'realEstate', name: 'Real Estate Agent', icon: '🏠', analogies: [
    'Location, location, location — in investing it is timing, diversification, allocation.',
    'Market cycles in real estate mirror financial markets — buy when others are fearful.',
    'Rental yield is like dividend income — asset pays you while you hold it.',
    'Leverage in property is like margin in investing — amplifies both gains and risk.'
  ]},
  { id: 'military', name: 'Military / Security', icon: '🎖️', analogies: [
    'Asset allocation is your tactical formation — strength in multiple positions.',
    'Stop-loss orders are your retreat protocol — know when to exit before casualties mount.',
    'Intelligence gathering is market research — act on information, not emotion.',
    'Long-term strategy over short-term battles — win the war, not just one trade.'
  ]},
  { id: 'driver', name: 'Driver / Logistics', icon: '🚚', analogies: [
    'Portfolio rebalancing is route optimization — efficiency keeps you on track.',
    'Long-term investing is a long haul — steady pace beats erratic sprinting.',
    'Diversification is carrying multiple loads — one delayed delivery does not kill the business.',
    'Market research is knowing your route — no surprises when you have mapped it.'
  ]},
  { id: 'musician', name: 'Musician / Artist', icon: '🎵', analogies: [
    'Investing is composing a symphony — every instrument (asset) plays a role in the whole.',
    'Consistency is practice — 10,000 hours in the market compounds like 10,000 hours of skill.',
    'Diversification is your setlist — not every song is a hit, but the show must go on.',
    'Patience is timing in music — the pause between notes is as important as the notes.'
  ]},
  { id: 'entrepreneur', name: 'Entrepreneur / Business', icon: '🚀', analogies: [
    'Investing your capital is like funding your own startup — controlled risk, measured returns.',
    'Diversification is your product portfolio — one product failure does not kill the company.',
    'Cash flow management in business mirrors dividend reinvestment — keep capital working.',
    'Market research is your business intelligence — data-driven decisions outperform gut instinct.'
  ]},
  { id: 'psychologist', name: 'Psychologist', icon: '🧠', analogies: [
    'Behavioral finance is your specialty — you already know emotions drive bad decisions.',
    'Long-term thinking is cognitive reframing — see the big picture, not the daily noise.',
    'Market fear is fight-or-flight — recognize it, do not act on it.',
    'Systematic investing removes cognitive bias — the system decides, not the moment.'
  ]},
  { id: 'police', name: 'Police / Law Enforcement', icon: '🚔', analogies: [
    'Risk management is your patrol protocol — anticipate, prevent, respond.',
    'Diversification is backup — you never go in alone.',
    'Market intelligence is your informant network — the more sources, the clearer the picture.',
    'Stop-loss is your tactical retreat — live to fight another day.'
  ]},
  { id: 'scientist', name: 'Scientist / Researcher', icon: '🔬', analogies: [
    'Investing is hypothesis testing — thesis, entry, data, exit based on evidence.',
    'Diversification is experimental controls — isolate variables, do not bet everything on one.',
    'Backtesting is peer review — validate your strategy before publishing (deploying) it.',
    'Compounding is exponential growth curves — you have seen the graphs, now live them.'
  ]},
  { id: 'retail', name: 'Retail / Sales', icon: '🛒', analogies: [
    'Investing at market lows is buying inventory on sale — same quality, lower price.',
    'Dividends are like repeat customers — they come back without you doing anything.',
    'Diversification is your product range — one SKU going out of stock does not close the store.',
    'Patience is inventory turnover — some assets move fast, some slow, all contribute.'
  ]}
];

// ── MOCK COURSES ──────────────────────────────────────────────────────────────
const MOCK_COURSES = [
  {
    id: 1, order: 1, title: 'Sales Fundamentals',
    description: 'Core sales techniques for financial product agents. Covers the 7-step conversion framework, objection handling, and trust-building language.',
    lessons: [
      { id: 101, title: 'Introduction to Sales Psychology', content: '<h2>Understanding the Client Mindset</h2><p>Before making any pitch, understand what drives your client. Financial decisions are 80% emotional, 20% rational. Your role is to create confidence and reduce fear.</p><h3>The 3 Core Motivators</h3><ul><li><strong>Security</strong> — clients want to protect what they have</li><li><strong>Growth</strong> — clients want more than they started with</li><li><strong>Status</strong> — clients want to feel smart about their decisions</li></ul><p>Mirror the motivator back to them in your language.</p>', video_url: null, images: [] },
      { id: 102, title: 'The 7-Step Conversion Framework', content: '<h2>From Lead to Deposit</h2><ol><li><strong>Open with context</strong> — reference their signup source to build familiarity</li><li><strong>Qualify intent</strong> — two questions to confirm investment goals</li><li><strong>Build credibility</strong> — one sharp market insight relevant to them</li><li><strong>Handle top 3 objections</strong> — see objection matrix in lesson 4</li><li><strong>Present the offer</strong> — minimum deposit, supported payment methods</li><li><strong>Time the ask</strong> — end-of-week or before market open works best</li><li><strong>Log and tag</strong> — DP tag in CRM immediately on verbal confirmation</li></ol>', video_url: null, images: [] },
      { id: 103, title: 'Trust-Building Language', content: '<h2>Words That Build Confidence</h2><p>Certain phrases accelerate trust. Others destroy it. This lesson covers the approved vocabulary and the banned phrases list.</p><h3>Use These</h3><ul><li>"Based on your goals, here is what I recommend…"</li><li>"Many of our clients in your situation found that…"</li><li>"Let me show you exactly how this works…"</li></ul><h3>Avoid These</h3><ul><li>"You should definitely…" (creates pressure)</li><li>"I guarantee…" (compliance risk)</li><li>"Everyone is doing it…" (sounds desperate)</li></ul>', video_url: null, images: [] }
    ],
    questions: [
      { question: 'What percentage of financial decisions are emotional?', options: ['20%', '50%', '80%', '100%'], correct: 2 },
      { question: 'What is Step 6 in the 7-step conversion framework?', options: ['Handle objections', 'Log in CRM', 'Time the ask', 'Qualify intent'], correct: 2 },
      { question: 'Which phrase is on the approved vocabulary list?', options: ['"I guarantee results"', '"Everyone is doing it"', '"Based on your goals, here is what I recommend"', '"You should definitely invest now"'], correct: 2 },
      { question: 'What are the 3 core client motivators?', options: ['Price, speed, quality', 'Security, growth, status', 'Trust, authority, scarcity', 'Logic, emotion, habit'], correct: 1 },
      { question: 'When should you apply the DP tag in CRM?', options: ['After funds clear', 'On verbal confirmation immediately', 'End of day', 'When manager approves'], correct: 1 }
    ]
  },
  {
    id: 2, order: 2, title: 'CRM System Mastery',
    description: 'Full training on GNM CRM: tags, statuses, activity logging, scheduled calls, and the client lifecycle.',
    lessons: [
      { id: 201, title: 'Tag System Deep Dive', content: '<h2>Understanding CRM Tags</h2><p>Every client has a status tag. Accurate tagging drives correct routing, reporting, and follow-up scheduling.</p><h3>Tag Definitions</h3><ul><li><strong>DP</strong> — Deposit confirmed. Client has funded. Retention team takes over within 48h.</li><li><strong>LD</strong> — Lead. Interest confirmed, no deposit yet. Your primary target.</li><li><strong>NA</strong> — No Answer. Log attempt count. Auto-escalate after 5 NAs.</li><li><strong>SC</strong> — Scheduled Call. Future callback booked. Never miss an SC.</li><li><strong>RF</strong> — Referral. Came via existing client. Warm lead — higher conversion rate.</li><li><strong>TR</strong> — Transfer. Client passed to senior agent or retention.</li></ul>', video_url: null, images: [] },
      { id: 202, title: 'Activity Logging Standards', content: '<h2>Logging Rules</h2><p>Every client interaction must be logged within 2 hours. Unlogged activity is invisible to management and cannot be used in dispute resolution.</p><h3>What to Log</h3><ul><li>Every call: duration, outcome, next action</li><li>Every WhatsApp exchange: summary + timestamp</li><li>Every email: subject + outcome</li><li>Every deposit discussion: amount mentioned, client hesitation</li></ul><p>Use the Notes field. Be specific. "Called client — no answer" is NOT sufficient. "Called client 14:32 — 6 rings, no answer, left voicemail re: account verification. Will retry 16:00" IS sufficient.</p>', video_url: null, images: [] }
    ],
    questions: [
      { question: 'What does the NA tag mean?', options: ['New Account', 'No Answer', 'Not Available', 'Not Approved'], correct: 1 },
      { question: 'Within how many hours must activity be logged?', options: ['1 hour', '2 hours', '24 hours', 'Same day'], correct: 1 },
      { question: 'What happens after 5 consecutive NA tags?', options: ['Client is deleted', 'Auto-escalate', 'Transfer to retention', 'Mark as inactive'], correct: 1 },
      { question: 'Which tag indicates a warm lead from an existing client?', options: ['DP', 'SC', 'RF', 'TR'], correct: 2 },
      { question: 'A call log saying only "Called client — no answer" is:', options: ['Acceptable', 'Excellent', 'Not sufficient', 'Required format'], correct: 2 }
    ]
  },
  {
    id: 3, order: 3, title: 'Compliance & Data Protection',
    description: 'Q2 2026 compliance requirements. Mandatory for all agents. Covers PII handling, call recording rules, and escalation procedures.',
    lessons: [
      { id: 301, title: 'PII Retention Policy', content: '<h2>Client Data Rules</h2><p>Client personally identifiable information (PII) must not be retained beyond <strong>90 days</strong> after account closure. This is a regulatory requirement.</p><h3>What Counts as PII</h3><ul><li>Full name + date of birth combination</li><li>National ID / passport numbers</li><li>Bank account or card details</li><li>Home address</li><li>Voice recordings</li></ul><p>Do not store any PII outside the CRM. No screenshots on personal devices. No WhatsApp exports to personal storage.</p>', video_url: null, images: [] },
      { id: 302, title: 'Call Recording & Screen Capture', content: '<h2>Recording Requirements</h2><p>Effective Q2 2026, screen recording is <strong>mandatory</strong> during all client calls. The recording must start before the call connects and end after you hang up.</p><h3>Storage</h3><p>Recordings auto-upload to the secure server. Never save locally beyond the session. Never share recordings externally.</p><h3>Exceptions</h3><p>Technical failure must be reported to your TL within 30 minutes of the session. Log the failure in CRM notes with timestamp.</p>', video_url: null, images: [] },
      { id: 303, title: 'Escalation Procedures', content: '<h2>When to Escalate</h2><p>Some situations require immediate escalation to your Team Leader or Compliance Officer. Know the list.</p><h3>Mandatory Escalations</h3><ul><li>Client claims they were misled or given guarantees</li><li>Client is under 18 (terminate call, escalate immediately)</li><li>Suspicious deposit pattern (multiple small deposits, unusual sources)</li><li>Client requests to withdraw within 48h of deposit</li><li>Any threat of legal action or regulator complaint</li></ul><p>Log all escalations in CRM with time, reason, and TL name notified.</p>', video_url: null, images: [] }
    ],
    questions: [
      { question: 'How long can PII be retained after account closure?', options: ['30 days', '60 days', '90 days', '180 days'], correct: 2 },
      { question: 'When must screen recording start?', options: ['When client speaks', 'Before the call connects', 'After client consents', 'When deposit is discussed'], correct: 1 },
      { question: 'A client under 18 calls in. You should:', options: ['Continue the call normally', 'Transfer to senior agent', 'Terminate call and escalate immediately', 'Ask for parent consent'], correct: 2 },
      { question: 'What constitutes sufficient WhatsApp logging?', options: ['Just the timestamp', 'Summary and timestamp', 'The full transcript must be pasted', 'Only if client requests it'], correct: 1 },
      { question: 'Technical recording failure must be reported within:', options: ['15 minutes', '30 minutes', '1 hour', '2 hours'], correct: 1 }
    ]
  },
  {
    id: 4, order: 4, title: 'Objection Handling Masterclass',
    description: 'Advanced objection handling for the most common client pushbacks. Real scenarios from Q1 top performers.',
    lessons: [
      { id: 401, title: 'The Objection Matrix', content: '<h2>Top 10 Objections and Responses</h2><p>Every objection is a hidden question. Your job is to answer the real question, not the surface one.</p><h3>Objection 1: "I need to think about it"</h3><p><em>Real question:</em> "Am I making the right decision?" <br><em>Response:</em> "Of course — what specific part would you like to think through? I can walk you through it right now so your decision is fully informed."</p><h3>Objection 2: "I cannot afford it right now"</h3><p><em>Real question:</em> "Is the minimum too high for me?" <br><em>Response:</em> "Our entry position is designed to be accessible. What amount are you comfortable starting with? Even a smaller position gets you in and learning."</p><h3>Objection 3: "Is this safe?"</h3><p><em>Real question:</em> "Will I lose my money?" <br><em>Response:</em> "Safety is built into how you position. Let me show you how the risk management tools work — you are always in control."</p>', video_url: null, images: [] },
      { id: 402, title: 'Reframe Techniques', content: '<h2>Reframing the Conversation</h2><p>Reframing turns a negative into a question you can answer. Three techniques:</p><h3>1. Feel-Felt-Found</h3><p>"I understand how you feel. Many clients felt the same way initially. What they found was that once they saw the first result, their confidence changed completely."</p><h3>2. Third-Party Reference</h3><p>"A client in a very similar situation started with exactly that concern. Within 60 days they came back to increase their position. I cannot share their details but I can share what made the difference."</p><h3>3. Future Pacing</h3><p>"Picture yourself six months from now having made this decision. What would need to be true for you to look back and say it was the right call?"</p>', video_url: null, images: [] }
    ],
    questions: [
      { question: 'What is the real question behind "I need to think about it"?', options: ['"I do not trust you"', '"Am I making the right decision?"', '"The price is too high"', '"I need to ask my spouse"'], correct: 1 },
      { question: 'The Feel-Felt-Found technique is:', options: ['A closing technique', 'A reframing technique', 'A compliance procedure', 'A CRM tag method'], correct: 1 },
      { question: 'Future Pacing involves:', options: ['Scheduling a callback', 'Referencing past clients', 'Picturing a positive future outcome', 'Reducing the minimum deposit'], correct: 2 },
      { question: 'When a client says "I cannot afford it", the best first response is:', options: ['End the call politely', 'Ask what amount they are comfortable with', 'Offer a free trial', 'Escalate to senior agent'], correct: 1 },
      { question: 'Third-Party Reference is used to:', options: ['Verify client identity', 'Show social proof without disclosing details', 'Log the objection in CRM', 'Escalate to compliance'], correct: 1 }
    ]
  }
];

// ── MOCK AGENT DATA (manager view) ────────────────────────────────────────────
const MOCK_AGENTS = [
  { id: 1, name: 'Alex Morgan', courses_completed: 3, avg_score: 84, last_exam_date: '2026-03-28', status: 'active', last_active: new Date(Date.now() - 1 * 86400000).toISOString(), history: [
    { course_id: 1, course_title: 'Sales Fundamentals', score: 90, status: 'completed', exam_date: '2026-03-15' },
    { course_id: 2, course_title: 'CRM System Mastery', score: 80, status: 'completed', exam_date: '2026-03-22' },
    { course_id: 3, course_title: 'Compliance & Data Protection', score: 82, status: 'completed', exam_date: '2026-03-28' },
    { course_id: 4, course_title: 'Objection Handling', score: null, status: 'in-progress', exam_date: null }
  ]},
  { id: 2, name: 'Dana Kim', courses_completed: 4, avg_score: 91, last_exam_date: '2026-03-30', status: 'active', last_active: new Date(Date.now() - 0.5 * 86400000).toISOString(), history: [
    { course_id: 1, course_title: 'Sales Fundamentals', score: 95, status: 'completed', exam_date: '2026-03-10' },
    { course_id: 2, course_title: 'CRM System Mastery', score: 88, status: 'completed', exam_date: '2026-03-17' },
    { course_id: 3, course_title: 'Compliance & Data Protection', score: 90, status: 'completed', exam_date: '2026-03-24' },
    { course_id: 4, course_title: 'Objection Handling', score: 91, status: 'completed', exam_date: '2026-03-30' }
  ]},
  { id: 3, name: 'Marcus Webb', courses_completed: 1, avg_score: 62, last_exam_date: '2026-03-05', status: 'struggling', last_active: new Date(Date.now() - 10 * 86400000).toISOString(), history: [
    { course_id: 1, course_title: 'Sales Fundamentals', score: 62, status: 'completed', exam_date: '2026-03-05' },
    { course_id: 2, course_title: 'CRM System Mastery', score: null, status: 'not-started', exam_date: null },
    { course_id: 3, course_title: 'Compliance & Data Protection', score: null, status: 'not-started', exam_date: null },
    { course_id: 4, course_title: 'Objection Handling', score: null, status: 'not-started', exam_date: null }
  ]},
  { id: 4, name: 'Priya Nair', courses_completed: 2, avg_score: 77, last_exam_date: '2026-03-25', status: 'active', last_active: new Date(Date.now() - 2 * 86400000).toISOString(), history: [
    { course_id: 1, course_title: 'Sales Fundamentals', score: 75, status: 'completed', exam_date: '2026-03-18' },
    { course_id: 2, course_title: 'CRM System Mastery', score: 79, status: 'completed', exam_date: '2026-03-25' },
    { course_id: 3, course_title: 'Compliance & Data Protection', score: null, status: 'in-progress', exam_date: null },
    { course_id: 4, course_title: 'Objection Handling', score: null, status: 'not-started', exam_date: null }
  ]},
  { id: 5, name: 'Jordan Lee', courses_completed: 0, avg_score: null, last_exam_date: null, status: 'not-started', last_active: new Date(Date.now() - 9 * 86400000).toISOString(), history: [
    { course_id: 1, course_title: 'Sales Fundamentals', score: null, status: 'not-started', exam_date: null },
    { course_id: 2, course_title: 'CRM System Mastery', score: null, status: 'not-started', exam_date: null },
    { course_id: 3, course_title: 'Compliance & Data Protection', score: null, status: 'not-started', exam_date: null },
    { course_id: 4, course_title: 'Objection Handling', score: null, status: 'not-started', exam_date: null }
  ]},
  { id: 6, name: 'Sofia Reyes', courses_completed: 3, avg_score: 88, last_exam_date: '2026-03-29', status: 'active', last_active: new Date(Date.now() - 1 * 86400000).toISOString(), history: [
    { course_id: 1, course_title: 'Sales Fundamentals', score: 92, status: 'completed', exam_date: '2026-03-14' },
    { course_id: 2, course_title: 'CRM System Mastery', score: 85, status: 'completed', exam_date: '2026-03-21' },
    { course_id: 3, course_title: 'Compliance & Data Protection', score: 87, status: 'completed', exam_date: '2026-03-29' },
    { course_id: 4, course_title: 'Objection Handling', score: null, status: 'exam-due', exam_date: null }
  ]}
];

export default {
  name: 'Instruction1Container',

  components: { VikiFace, C1ModCalendar },

  data() {
    return {
      c1Status: 'shift',
      c1StatusSince: 0,
      c1StatusElapsed: '00:00',
      c1StatusMenuOpen: false,
      _c1StatusInterval: null,
      c1HamMenuOpen: false,
      c1ShiftMenuOpen: false,
      c1ShiftElapsed: '00:00',
      c1ShiftStartTime: 0,
      c1BathroomCount: 0,
      c1CoffeeCount: 0,
      c1WeekLog: [],
      c1NavMode: 'panels',
      c1VikiMood: 'neutral',
      c1VikiSpeaking: false,
      // UI top-strip
      c1TopTab: null,
      // View: 'agent' or 'manager'
      i1View: 'agent',

      // Panel
      i1SlideOpen: 'nav',
      i1PanelActiveContent: null,

      // Toast
      i1Toast: null,
      i1ToastTimer: null,

      // Loading states
      i1Loading: false,
      i1MgrLoading: false,
      i1FormLoading: false,

      // Courses + lessons
      i1Courses: [],
      i1CourseLessons: {},  // { courseId: [lessons] }
      i1LessonsLoading: {}, // { courseId: bool }
      i1ExpandedCourse: null,

      // Progress tracking (localStorage)
      i1ReadLessons: {},   // { 'courseId_lessonId': true }
      i1ExamScores: {},    // { courseId: [scores] }
      i1CourseStarted: {}, // { courseId: true }

      // Settings (manager)
      i1Settings: {
        dailyExamQuestions: 5,
        examTimerSeconds: 30,
        mandatoryExam: false,
        linearProgress: true,
        notifyBehind: true,
        weeklyDigest: false,
        welcomeText: '',
        examInstruction: '',
      },
      i1SettingsSaving: false,

      // Filters
      i1RawSearch: '',
      i1SearchQuery: '',
      i1SearchTimer: null,
      i1FilterStatus: 'all',
      i1FilterMinScore: 0,
      i1FilterDateFrom: '',
      i1FilterDateTo: '',
      i1StatusFilters: [
        { id: 'all', label: 'All' },
        { id: 'not-started', label: 'Not Started' },
        { id: 'in-progress', label: 'In Progress' },
        { id: 'completed', label: 'Completed' },
        { id: 'exam-due', label: 'Exam Due' }
      ],

      // Exam modal
      i1ExamModal: null,
      i1ExamQIdx: 0,
      i1ExamAnswers: {},
      i1ExamAnswerRevealed: false,
      i1ExamTimer: 60,
      i1ExamTimerHandle: null,
      i1ExamResult: null,

      // Lesson accordion (inline — replaces drawer popup)
      i1ExpandedLesson: null, // key: courseId + '-' + lessonId

      // Add course drawer
      i1AddCourseOpen: false,
      i1EditingCourse: null,
      i1NewCourse: { title: '', description: '', order: 1, lessons: [], questions: [] },

      // Manager view
      i1Agents: [],
      i1AgentDetail: null,
      i1MgrSort: 'progress',
      i1MgrSorts: [
        { id: 'progress', label: 'Progress ↓' },
        { id: 'score', label: 'Score ↓' },
        { id: 'last_active', label: 'Last Active' },
        { id: 'name', label: 'Name A-Z' }
      ],

      // Professions / analogies
      i1Professions: PROFESSIONS,
      i1OpenProf: null, // inline analogy card (replaces modal)

      // Daily exam
      i1HasDailyExam: false,
      i1DailyExamCourse: null,

      // Viki messages
      i1VikiMessages: [],

      // Author color pool
      i1AuthorColors: ['#6366f1','#8b5cf6','#06b6d4','#10b981','#f59e0b','#ef4444','#ec4899','#3b82f6']
    };
  },

  computed: {
    c1CurrentPageLabel() {
      const map = {
        '/clients1':'Clients', '/tasks1':'Tasks', '/stats1':'Statistics',
        '/chat1':'Chat', '/communications1':'Communications', '/affiliation1':'Affiliation',
        '/finance1':'Finance', '/shifts1':'Shifts', '/games1':'Games',
        '/users1':'Users', '/brokers1':'Brokers', '/security1':'Security',
        '/quiz1':'Quiz', '/instruction1':'Instructions', '/settings1':'Settings', '/profile1':'My Profile',
      };
      return map[this.$route.path] || 'Menu';
    },
    c1UserInitials() {
      const u = this.$store.state.user && this.$store.state.user.auth && this.$store.state.user.auth.user;
      if (!u) return "?";
      return ((u.first_name || "")[0] || "").toUpperCase() + ((u.last_name || "")[0] || "").toUpperCase();
    },
    c1UserFullName() {
      const u = this.$store.state.user && this.$store.state.user.auth && this.$store.state.user.auth.user;
      if (!u) return "User";
      return [u.first_name, u.last_name].filter(Boolean).join(" ") || "User";
    },
    c1RoleLabel() {
      const u = this.$store.state.user && this.$store.state.user.auth && this.$store.state.user.auth.user;
      if (!u) return "";
      const map = { admin: "Admin", manager: "Manager", agent: "Agent", broker: "Broker" };
      return map[u.role] || u.role || "";
    },
    c1TzUK() { return this.c1TzTime("Europe/London"); },
    c1TzIL() { return this.c1TzTime("Asia/Jerusalem"); },
    c1TzCB() { return this.c1TzTime("America/Cayman"); },

    i1PanelIsOpen() {
      return !!this.i1SlideOpen;
    },
    i1PanelAtContent() {
      return !!this.i1SlideOpen && this.i1SlideOpen !== 'nav' && this.i1SlideOpen !== 'menu';
    },
    i1ActivePanelLabel() {
      const labels = { filters: 'Filters', charts: 'Analytics', viki: 'Viki' };
      return (this.i1SlideOpen && labels[this.i1SlideOpen]) ? labels[this.i1SlideOpen] : 'Panels';
    },
    i1UserInitials() {
      const user = (this.$store.state.user && this.$store.state.user.auth && this.$store.state.user.auth.user) || {};
      return (((user.first_name || '')[0] || '').toUpperCase() + ((user.last_name || '')[0] || '').toUpperCase()) || 'U';
    },
    i1UserFullName() {
      const user = (this.$store.state.user && this.$store.state.user.auth && this.$store.state.user.auth.user) || {};
      return `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'User';
    },
    i1RoleLabel() {
      const map = { 1:'Admin',2:'Team Leader',3:'Manager',4:'Senior Agent',5:'Agent',6:'Junior Agent',7:'New Agent',8:'Support',9:'Retention',10:'Finance',12:'Super Admin' };
      const user = (this.$store.state.user && this.$store.state.user.auth && this.$store.state.user.auth.user) || {};
      return map[user.role] || 'Agent';
    },
    i1IsAdmin() {
      const user = (this.$store.state.user && this.$store.state.user.auth && this.$store.state.user.auth.user) || {};
      return [1,2,3,12].includes(user.role);
    },
    i1IsManager() {
      const user = (this.$store.state.user && this.$store.state.user.auth && this.$store.state.user.auth.user) || {};
      return [1,2,3,12].includes(user.role);
    },

    i1FilteredCourses() {
      let list = this.i1Courses;
      if (this.i1FilterStatus !== 'all') {
        list = list.filter(c => this.i1CourseStatusSlug(c) === this.i1FilterStatus);
      }
      if (this.i1SearchQuery) {
        const q = this.i1SearchQuery.toLowerCase();
        list = list.filter(c =>
          (c.title && c.title.toLowerCase().includes(q)) ||
          (c.description && c.description.toLowerCase().includes(q))
        );
      }
      return list;
    },

    i1SortedAgents() {
      const agents = [...this.i1Agents];
      if (this.i1MgrSort === 'progress') {
        return agents.sort((a, b) => b.courses_completed - a.courses_completed);
      } else if (this.i1MgrSort === 'score') {
        return agents.sort((a, b) => (b.avg_score || 0) - (a.avg_score || 0));
      } else if (this.i1MgrSort === 'last_active') {
        return agents.sort((a, b) => new Date(b.last_active || 0) - new Date(a.last_active || 0));
      } else {
        return agents.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }
    },

    i1CompletionChart() {
      return this.i1Agents.slice(0, 8).map(a => ({
        name: (a.name || '').split(' ')[0],
        pct: this.i1Courses.length > 0 ? Math.round((a.courses_completed / this.i1Courses.length) * 100) : 0
      }));
    },

    i1ScoreChart() {
      return this.i1Courses.map(c => ({
        name: c.title.length > 20 ? c.title.slice(0, 18) + '…' : c.title,
        pct: this.i1AvgScoreForCourse(c.id)
      }));
    },

    i1ScoreTrendPoints() {
      const scores = this.i1MyRecentScores();
      if (!scores.length) return '';
      const step = 200 / Math.max(scores.length - 1, 1);
      return scores.map((s, i) => `${i * step},${60 - (s / 100) * 55}`).join(' ');
    },

    i1ScoreTrendDots() {
      const scores = this.i1MyRecentScores();
      if (!scores.length) return [];
      const step = 200 / Math.max(scores.length - 1, 1);
      return scores.map((s, i) => ({ x: i * step, y: 60 - (s / 100) * 55 }));
    }
  },

  watch: {
    i1RawSearch(val) {
      clearTimeout(this.i1SearchTimer);
      this.i1SearchTimer = setTimeout(() => { this.i1SearchQuery = val.trim(); }, 300);
    },
    i1ExamModal(val) {
      if (!val) this.i1ClearExamTimer();
    },
    'i1SlideOpen': function(val) {
      if (val && val !== 'nav' && val !== 'menu') {
        this.i1PanelActiveContent = val;
      }
    }
  },

  created() {
    const _keepNav = sessionStorage.getItem('c1keepNav'); if (_keepNav) sessionStorage.removeItem('c1keepNav');
    const _openPanel = sessionStorage.getItem('c1openPanel'); if (_openPanel) sessionStorage.removeItem('c1openPanel');
    if (_openPanel) { this.i1SlideOpen = 'filters'; this.i1PanelActiveContent = 'filters'; }
    else if (_keepNav) { this.i1SlideOpen = 'nav'; this.c1NavMode = 'panels'; }
    this.i1LoadProgress();
    this.i1LoadCourses();
    this.i1BuildVikiMessages();
  },

  beforeDestroy() {
    if (this._c1StatusInterval) clearInterval(this._c1StatusInterval);
    this.i1ClearExamTimer();
  },

  mounted() {
    document.documentElement.style.setProperty('--gnm-sw', '0px');
    const savedStatus = localStorage.getItem('c1_status');
    const savedSince  = localStorage.getItem('c1_status_since');
    const today = new Date().toISOString().slice(0, 10);
    const savedShiftDay = localStorage.getItem('c1_shift_day');
    if (savedShiftDay !== today) {
      this.c1ShiftStartTime = Date.now();
      this.c1BathroomCount = 0;
      this.c1CoffeeCount = 0;
      localStorage.setItem('c1_status', 'shift');
      localStorage.setItem('c1_status_since', String(Date.now()));
      localStorage.setItem('c1_shift_start', String(Date.now()));
      localStorage.setItem('c1_shift_day', today);
      localStorage.setItem('c1_bathroom_count', '0');
      localStorage.setItem('c1_coffee_count', '0');
    } else {
      if (savedStatus) this.c1Status = savedStatus;
      this.c1ShiftStartTime = parseInt(localStorage.getItem('c1_shift_start') || String(Date.now()), 10);
      this.c1BathroomCount = parseInt(localStorage.getItem('c1_bathroom_count') || '0', 10);
      this.c1CoffeeCount   = parseInt(localStorage.getItem('c1_coffee_count') || '0', 10);
    }
    this.c1StatusSince = savedSince ? parseInt(savedSince, 10) : Date.now();
    this._c1StatusInterval = setInterval(this.c1TickStatus, 1000);
  },

  methods: {
    c1SetStatus(s) {
      if (s === 'bathroom') this.c1BathroomCount++;
      if (s === 'coffee')   this.c1CoffeeCount++;
      this.c1Status = s;
      this.c1StatusSince = Date.now();
      this.c1StatusElapsed = '00:00';
      this.c1ShiftMenuOpen = false;
      localStorage.setItem('c1_status', s);
      localStorage.setItem('c1_status_since', String(this.c1StatusSince));
      if (s === 'bathroom') localStorage.setItem('c1_bathroom_count', String(this.c1BathroomCount));
      if (s === 'coffee')   localStorage.setItem('c1_coffee_count',   String(this.c1CoffeeCount));
      if (s === 'end') this.c1SaveTodayToLog();
    },
    c1TickStatus() {
      const fmtSec = (sec) => {
        const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s2 = sec % 60;
        return h > 0
          ? String(h).padStart(2,'0') + ':' + String(m).padStart(2,'0') + ':' + String(s2).padStart(2,'0')
          : String(m).padStart(2,'0') + ':' + String(s2).padStart(2,'0');
      };
      this.c1StatusElapsed = fmtSec(Math.floor((Date.now() - this.c1StatusSince) / 1000));
      this.c1ShiftElapsed  = fmtSec(Math.floor((Date.now() - this.c1ShiftStartTime) / 1000));
    },
    c1SaveTodayToLog() {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const workedMin = Math.floor((Date.now() - this.c1ShiftStartTime) / 60000);
        const log = JSON.parse(localStorage.getItem('c1_week_log') || '[]').filter(r => r.date !== today);
        log.unshift({ date: today, workedMin: Math.max(0, workedMin), bathroom: this.c1BathroomCount, coffee: this.c1CoffeeCount });
        localStorage.setItem('c1_week_log', JSON.stringify(log.slice(0, 14)));
        this.c1WeekLog = log.slice(0, 14);
      } catch(e) {}
    },
    c1TzTime(tz) {
      try {
        return new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: tz });
      } catch(e) { return "--:--"; }
    },
    c1Logout() {
      this.$store.dispatch('user/logOut');
    },

    // ── API / DATA LOADING ──────────────────────────────────────────────────

    async i1LoadCourses() {
      this.i1Loading = true;
      try {
        // Backend route: GET users/questions_user_exam/{exam_type}
        // const res = await fetch(`${API}/users/questions_user_exam/all`, { headers: auth() });
        // this.i1Courses = await res.json();
        await new Promise(r => setTimeout(r, 280));
        this.i1Courses = MOCK_COURSES.map(c => ({ ...c, lessons: undefined, questions: undefined, _lessons: c.lessons, _questions: c.questions }));
        // Pre-load lesson cache from mock
        MOCK_COURSES.forEach(c => { this.$set(this.i1CourseLessons, c.id, c._lessons || c.lessons || []); });
        this.i1Courses = MOCK_COURSES;
        this.i1CheckDailyExam();
      } catch (e) {
        console.error('i1LoadCourses error', e);
        this.i1Courses = MOCK_COURSES;
      } finally {
        this.i1Loading = false;
      }
    },

    async i1LoadLessons(courseId) {
      if (this.i1CourseLessons[courseId] && this.i1CourseLessons[courseId].length) return;
      this.$set(this.i1LessonsLoading, courseId, true);
      try {
        // Backend route: GET users/questions_user_exam/{courseId}
        // const res = await fetch(`${API}/users/questions_user_exam/${courseId}`, { headers: auth() });
        // const data = await res.json();
        await new Promise(r => setTimeout(r, 200));
        const course = MOCK_COURSES.find(c => c.id === courseId);
        this.$set(this.i1CourseLessons, courseId, (course && course.lessons) || []);
      } catch (e) {
        console.error('i1LoadLessons error', e);
      } finally {
        this.$set(this.i1LessonsLoading, courseId, false);
      }
    },

    async i1LoadManagerData() {
      if (this.i1Agents.length) return;
      this.i1MgrLoading = true;
      try {
        // Backend route: GET users/table (agent scores embedded in user table data)
        await new Promise(r => setTimeout(r, 350));
        this.i1Agents = MOCK_AGENTS;
      } catch (e) {
        console.error('i1LoadManagerData error', e);
        this.i1Agents = MOCK_AGENTS;
      } finally {
        this.i1MgrLoading = false;
      }
    },

    // ── PROGRESS PERSISTENCE ────────────────────────────────────────────────

    i1LoadProgress() {
      this.i1ReadLessons  = JSON.parse(localStorage.getItem('i1_read') || '{}');
      this.i1ExamScores   = JSON.parse(localStorage.getItem('i1_scores') || '{}');
      this.i1CourseStarted = JSON.parse(localStorage.getItem('i1_started') || '{}');
    },

    i1SaveProgress() {
      localStorage.setItem('i1_read',    JSON.stringify(this.i1ReadLessons));
      localStorage.setItem('i1_scores',  JSON.stringify(this.i1ExamScores));
      localStorage.setItem('i1_started', JSON.stringify(this.i1CourseStarted));
    },

    i1IsLessonRead(courseId, lessonId) {
      return !!this.i1ReadLessons[`${courseId}_${lessonId}`];
    },

    i1MarkLessonRead(courseId, lessonId) {
      this.$set(this.i1ReadLessons, `${courseId}_${lessonId}`, true);
      this.i1SaveProgress();
      // Backend route: POST users/insert_to_answers_log { courseId, lessonId }
    },

    i1AllLessonsRead(course) {
      const lessons = this.i1CourseLessons[course.id] || [];
      return lessons.length > 0 && lessons.every(l => this.i1IsLessonRead(course.id, l.id));
    },

    i1CourseProgress(course) {
      const lessons = this.i1CourseLessons[course.id] || [];
      if (!lessons.length) return 0;
      const done = lessons.filter(l => this.i1IsLessonRead(course.id, l.id)).length;
      return Math.round((done / lessons.length) * 100);
    },

    i1CourseStatusSlug(course) {
      const scores = this.i1ExamScores[course.id] || [];
      const passed = scores.some(s => s >= 70);
      if (passed) return 'completed';
      if (this.i1AllLessonsRead(course)) return 'exam-due';
      if (this.i1CourseStarted[course.id]) return 'in-progress';
      return 'not-started';
    },

    i1CourseStatusLabel(course) {
      const map = {
        'completed': 'Completed',
        'exam-due': 'Exam Due',
        'in-progress': 'In Progress',
        'not-started': 'Not Started'
      };
      return map[this.i1CourseStatusSlug(course)] || 'Not Started';
    },

    i1LastScore(courseId) {
      const scores = this.i1ExamScores[courseId] || [];
      return scores.length ? scores[scores.length - 1] : null;
    },

    i1MyRecentScores() {
      const all = [];
      Object.values(this.i1ExamScores).forEach(arr => all.push(...arr));
      return all.slice(-7);
    },

    // ── COURSE ACTIONS ──────────────────────────────────────────────────────

    i1ToggleCourse(id) {
      this.i1ExpandedCourse = this.i1ExpandedCourse === id ? null : id;
      if (this.i1ExpandedCourse) this.i1LoadLessons(id);
    },

    i1StartCourse(course) {
      this.$set(this.i1CourseStarted, course.id, true);
      this.i1SaveProgress();
      this.i1ExpandedCourse = course.id;
      this.i1LoadLessons(course.id);
    },

    i1ContinueCourse(course) {
      this.i1ExpandedCourse = course.id;
      this.i1LoadLessons(course.id);
    },

    // ── LESSON ACCORDION ────────────────────────────────────────────────────

    i1ToggleLesson(course, lesson) {
      const key = course.id + '-' + lesson.id;
      if (this.i1ExpandedLesson === key) {
        this.i1ExpandedLesson = null;
      } else {
        this.$set(this.i1CourseStarted, course.id, true);
        this.i1SaveProgress();
        this.i1ExpandedLesson = key;
      }
    },

    i1IsLessonOpen(courseId, lessonId) {
      return this.i1ExpandedLesson === courseId + '-' + lessonId;
    },

    i1OpenNextLesson(course, currentIdx) {
      const lessons = this.i1CourseLessons[course.id] || [];
      if (currentIdx < lessons.length - 1) {
        const next = lessons[currentIdx + 1];
        this.i1ExpandedLesson = course.id + '-' + next.id;
      }
    },

    // ── EXAM SYSTEM ─────────────────────────────────────────────────────────

    i1CheckDailyExam() {
      // Check if any exam-due course exists
      const due = this.i1Courses.find(c => this.i1CourseStatusSlug(c) === 'exam-due');
      if (due) {
        this.i1HasDailyExam = true;
        this.i1DailyExamCourse = due;
      }
    },

    i1OpenDailyExam() {
      if (this.i1DailyExamCourse) this.i1StartExam(this.i1DailyExamCourse);
    },

    i1StartExam(course) {
      const q = course.questions || (MOCK_COURSES.find(c => c.id === course.id) || {}).questions || [];
      if (!q.length) {
        this.i1ShowToast('No exam questions available for this course.', '#ef4444');
        return;
      }
      // Shuffle questions
      const shuffled = [...q].sort(() => Math.random() - 0.5);
      this.i1ExamModal = { title: course.title + ' — Exam', questions: shuffled, courseId: course.id };
      this.i1ExamQIdx = 0;
      this.i1ExamAnswers = {};
      this.i1ExamAnswerRevealed = false;
      this.i1ExamResult = null;
      this.i1StartExamTimer();
    },

    i1StartExamTimer() {
      this.i1ClearExamTimer();
      this.i1ExamTimer = 60;
      this.i1ExamTimerHandle = setInterval(() => {
        this.i1ExamTimer--;
        if (this.i1ExamTimer <= 0) {
          this.i1ClearExamTimer();
          // Auto-advance on timeout
          if (this.i1ExamAnswers[this.i1ExamQIdx] === undefined) {
            this.i1ExamAnswers = { ...this.i1ExamAnswers, [this.i1ExamQIdx]: -1 }; // -1 = timed out
          }
          this.i1ExamNext();
        }
      }, 1000);
    },

    i1ClearExamTimer() {
      if (this.i1ExamTimerHandle) {
        clearInterval(this.i1ExamTimerHandle);
        this.i1ExamTimerHandle = null;
      }
    },

    i1SelectAnswer(optionIdx) {
      if (this.i1ExamAnswerRevealed) return;
      this.$set(this.i1ExamAnswers, this.i1ExamQIdx, optionIdx);
    },

    i1ExamNext() {
      this.i1ClearExamTimer();
      const questions = this.i1ExamModal.questions;
      if (this.i1ExamQIdx < questions.length - 1) {
        this.i1ExamQIdx++;
        this.i1ExamAnswerRevealed = false;
        this.i1StartExamTimer();
      } else {
        // Submit
        this.i1SubmitExam();
      }
    },

    async i1SubmitExam() {
      const questions = this.i1ExamModal.questions;
      const courseId = this.i1ExamModal.courseId;
      let correct = 0;
      questions.forEach((q, i) => {
        if (this.i1ExamAnswers[i] === q.correct) correct++;
      });
      const pct = Math.round((correct / questions.length) * 100);
      const passed = pct >= 70;

      // Save score
      const scores = this.i1ExamScores[courseId] || [];
      scores.push(pct);
      this.$set(this.i1ExamScores, courseId, scores);
      this.i1SaveProgress();

      // Backend route: POST users/insert_to_answers_log
      // await fetch(`${API}/users/insert_to_answers_log`, { method: 'POST', headers: auth(), body: JSON.stringify({ exam_type: courseId, answers: this.i1ExamAnswers, score: pct, passed }) });

      this.i1ExamResult = { correct, total: questions.length, pct, passed };

      if (passed) {
        this.i1HasDailyExam = false;
        this.i1BuildVikiMessages();
      }
    },

    // ── ANALOGY SYSTEM ──────────────────────────────────────────────────────

    i1ToggleAnalogy(prof) {
      if (this.i1OpenProf && this.i1OpenProf.profId === prof.id) {
        this.i1OpenProf = null;
        return;
      }
      const idx = Math.floor(Math.random() * prof.analogies.length);
      this.i1OpenProf = {
        profId: prof.id,
        profession: prof.name,
        all: prof.analogies,
        current: prof.analogies[idx],
        idx
      };
    },

    i1RefreshAnalogy() {
      if (!this.i1OpenProf) return;
      const next = (this.i1OpenProf.idx + 1) % this.i1OpenProf.all.length;
      this.i1OpenProf = {
        ...this.i1OpenProf,
        idx: next,
        current: this.i1OpenProf.all[next]
      };
    },

    // ── VIKI ────────────────────────────────────────────────────────────────

    i1BuildVikiMessages() {
      const msgs = [];
      // Daily exam notification
      const due = this.i1Courses.find(c => this.i1CourseStatusSlug(c) === 'exam-due');
      if (due) {
        msgs.push({
          type: 'exam',
          text: `You have a daily exam due! "${due.title}" is waiting for you.`,
          action: { label: 'Start Exam Now', fn: () => this.i1StartExam(due) }
        });
      }
      // Not-started reminder
      const notStarted = this.i1Courses.find(c => this.i1CourseStatusSlug(c) === 'not-started');
      if (notStarted) {
        msgs.push({
          type: 'reminder',
          text: `Reminder: You have not started "${notStarted.title}" yet.`,
          action: { label: 'Start Course', fn: () => { this.i1StartCourse(notStarted); this.i1SlideOpen = null; } }
        });
      }
      // Recent pass
      const recentPass = Object.entries(this.i1ExamScores).find(([, arr]) => arr[arr.length - 1] >= 70);
      if (recentPass) {
        const course = this.i1Courses.find(c => c.id === parseInt(recentPass[0]));
        if (course) {
          msgs.push({
            type: 'congrats',
            text: `Congratulations — you passed the "${course.title}" exam with ${recentPass[1][recentPass[1].length - 1]}%!`,
            action: null
          });
        }
      }
      this.i1VikiMessages = msgs;
    },

    // ── ADD COURSE (ADMIN) ──────────────────────────────────────────────────

    i1OpenAddCourse() {
      this.i1EditingCourse = null;
      this.i1NewCourse = { title: '', description: '', order: this.i1Courses.length + 1, lessons: [], questions: [] };
      this.i1AddCourseOpen = true;
    },

    i1AddLesson() {
      this.i1NewCourse.lessons.push({ title: '', content: '', video_url: '' });
    },

    i1RemoveLesson(idx) {
      this.i1NewCourse.lessons.splice(idx, 1);
    },

    i1AddQuestion() {
      this.i1NewCourse.questions.push({ question: '', options: ['', '', '', ''], correct: 0 });
    },

    i1RemoveQuestion(idx) {
      this.i1NewCourse.questions.splice(idx, 1);
    },

    async i1SaveCourse() {
      if (!this.i1NewCourse.title) return;
      this.i1FormLoading = true;
      try {
        // No backend course creation route — saved locally
        await new Promise(r => setTimeout(r, 350));
        const newCourse = {
          id: Date.now(),
          ...this.i1NewCourse
        };
        this.i1Courses.push(newCourse);
        this.$set(this.i1CourseLessons, newCourse.id, newCourse.lessons || []);
        this.i1AddCourseOpen = false;
        this.i1ShowToast('Course created successfully!', '#10b981');
      } catch (e) {
        console.error('i1SaveCourse error', e);
        this.i1ShowToast('Failed to save course.', '#ef4444');
      } finally {
        this.i1FormLoading = false;
      }
    },

    // ── MANAGER ─────────────────────────────────────────────────────────────

    i1OpenAgentDetail(agent) {
      this.i1AgentDetail = agent;
    },

    i1AgentInactive(agent) {
      if (!agent.last_active) return true;
      const days = (Date.now() - new Date(agent.last_active).getTime()) / 86400000;
      return days > 7;
    },

    i1AvgScoreForCourse(courseId) {
      const allScores = this.i1Agents
        .flatMap(a => (a.history || []).filter(h => h.course_id === courseId && h.score !== null).map(h => h.score));
      if (!allScores.length) return 0;
      return Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length);
    },

    // ── SETTINGS ─────────────────────────────────────────────────────────────

    async i1SaveSettings() {
      this.i1SettingsSaving = true;
      try {
        await this.$axios.post('/api/client/instruction/settings', this.i1Settings, {
          headers: { Authorization: 'Bearer ' + this.$store.getters['user/token'] }
        });
        this.i1ShowToast('Settings saved', '#22c55e');
      } catch (e) {
        this.i1ShowToast('Settings saved locally', '#22c55e');
      } finally {
        this.i1SettingsSaving = false;
      }
    },

    // ── PANEL ────────────────────────────────────────────────────────────────

    i1PanelBtnClick() {
      this.i1SlideOpen = this.i1PanelIsOpen ? null : 'nav';
    },
    i1SelectPanel(panel) {
      if (panel === 'nav') {
        this.i1SlideOpen = 'nav';
      } else {
        this.i1PanelActiveContent = panel;
        this.i1SlideOpen = panel;
      }
    },
    i1FilterPanelClick(e) {
      // Panel stays open after selecting a filter option — user uses back (←) or X to navigate/close
    },

    i1OpenSlide(name) {
      this.i1SlideOpen = this.i1SlideOpen === name ? null : name;
    },

    i1ProfNavClick(e) {
      if (e.target.closest('.i1-nb')) this.i1SlideOpen = null;
    },

    // ── TOAST ────────────────────────────────────────────────────────────────

    i1ShowToast(msg, color = '#6366f1') {
      clearTimeout(this.i1ToastTimer);
      this.i1Toast = { msg, color };
      this.i1ToastTimer = setTimeout(() => { this.i1Toast = null; }, 3200);
    },

    // ── HELPERS ──────────────────────────────────────────────────────────────

    i1AuthorInitials(name) {
      if (!name) return '?';
      const p = name.trim().split(' ');
      return (((p[0] || '')[0] || '').toUpperCase() + ((p[1] || '')[0] || '').toUpperCase()) || '?';
    },

    i1AuthorColor(name) {
      if (!name) return this.i1AuthorColors[0];
      let h = 0;
      for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
      return this.i1AuthorColors[Math.abs(h) % this.i1AuthorColors.length];
    },

    i1FormatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const p = n => String(n).padStart(2, '0');
      return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
    }
  }
};
</script>

<style scoped>
/* ── CSS VARIABLES ─────────────────────────────────────────────────────────── */
.i1-main {
  --i1-bg:      #ffffff;
  --i1-surface: #ffffff;
  --i1-surf2:   #f5f5f5;
  --i1-brd:     #e4e3df;
  --i1-brd-l:   #eeedea;
  --i1-t1:      #1a1a1a;
  --i1-t2:      #6b6963;
  --i1-t3:      #a09e98;
  --i1-ac:      #7c5cfc;
  /* Map --c1-* vars so shared panel CSS (background: var(--c1-card) etc.) resolves correctly */
  --c1-card:  #ffffff;
  --c1-bg:    #ffffff;
  --c1-brd:   #e4e3df;
  --c1-brd-l: #eeedea;
  --c1-t1:    #1a1a1a;
  --c1-t2:    #6b6963;
  --c1-t3:    #a09e98;
  --i1-ac2:     #9b7dfd;
  --i1-ac-glow: rgba(124,92,252,.1);
  --i1-green:   #2d9d5e;
  --i1-yellow:  #d4a017;
  --i1-red:     #c75450;
  --i1-blue:    #3b82f6;
  --i1-orange:  #e8891c;
  --i1-muted:   rgba(0,0,0,.05);
  --i1-new:     #2d9d5e;
  font-family: 'DM Sans', -apple-system, sans-serif;
  -webkit-font-smoothing: antialiased;
  font-size: 13px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  background: var(--i1-bg);
  color: var(--i1-t1);
  padding-top: 44px;
  padding-left: var(--gnm-sw, 220px);
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
}
@media (max-width: 767px) {
  .i1-main { padding-left: 0 !important; }
  /* Topbar: let sides be auto-width, center fills remaining space */
  .c1-tib-side { flex: 0 0 auto !important; }
  .c1-tib-center { flex: 1 !important; min-width: 0; overflow: hidden; }
  /* View toggle: icon-only, no background pill */
  .i1-view-toggle { background: transparent; border-color: transparent; padding: 0; gap: 1px; }
  .i1-vt-btn { padding: 6px 8px; font-size: 0; gap: 0; }
  .i1-vt-btn svg { width: 15px; height: 15px; }
  /* Content spacing */
  .i1-section-hdr { padding: 16px 12px 8px; }
  .i1-course-list { padding: 0 10px 16px; gap: 6px; }
  .i1-course-head { padding: 12px 12px; gap: 10px; }
  .i1-course-expanded { padding: 10px 12px 12px; }
  .i1-analogy-card { margin: 0; }
  .i1-analogy-card.i1-analogy-card-inline { margin: 0 0 4px; }
  .i1-lesson-body { padding: 12px; }
  /* Chips: 2-col grid on mobile */
  .i1-analogies-grid { grid-template-columns: repeat(2, 1fr); gap: 5px; padding: 0 12px 12px; }
}

/* ── TOAST ─────────────────────────────────────────────────────────────────── */
.in1-toast {
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
  padding: 10px 22px;
  border-radius: 24px;
  color: #fff;
  font-size: .85rem;
  font-weight: 600;
  box-shadow: 0 4px 20px rgba(0,0,0,.25);
  pointer-events: none;
  white-space: nowrap;
  height: auto;
  display: inline-flex;
  align-items: center;
}
.in1-toast-fade-enter-active, .in1-toast-fade-leave-active { transition: opacity .25s, transform .25s; }
.in1-toast-fade-enter, .in1-toast-fade-leave-to { opacity: 0; transform: translateX(-50%) translateY(10px); }

/* ── TOP BAR — uses global .c1-tib from c1-design.css ─────────────────────── */

/* View toggle */
.i1-view-toggle {
  display: flex;
  gap: 2px;
  background: var(--i1-surf2);
  border-radius: 8px;
  padding: 2px;
  border: 1px solid var(--i1-brd);
}
.i1-vt-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: var(--i1-t2);
  cursor: pointer;
  transition: background .13s, color .13s;
}
.i1-vt-btn svg { width: 12px; height: 12px; stroke: currentColor; fill: none; stroke-width: 2; }
.i1-vt-btn.on { background: var(--i1-ac); color: #fff; }
.i1-vt-btn:not(.on):hover { background: var(--i1-brd-l); color: var(--i1-t1); }

/* ── BACKDROP — defined globally in c1-design.css ──────────────────────────── */

/* ── PANEL NAV LIST (c1-pnav-*) ────────────────────────────────────────────── */
.c1-pnav-list { display: flex; flex-direction: column; }
.c1-pnav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: var(--c1-t1, #1a1a1a);
  transition: background .13s;
  border-left: 2px solid transparent;
}
.c1-pnav-item:hover { background: rgba(0,0,0,.04); }
.c1-pnav-item svg { width: 16px; height: 16px; stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; flex-shrink: 0; }
.c1-pnav-arr { width: 12px !important; height: 12px !important; stroke: var(--c1-t3, #a09e98) !important; margin-left: auto; flex-shrink: 0; }

/* ── PANELS ─────────────────────────────────────────────────────────────────── */
.i1-panel-hdr-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px 6px;
  border-bottom: 1px solid var(--i1-brd);
}
.i1-panel-hdr-bar svg { width: 14px; height: 14px; stroke: var(--i1-ac); fill: none; stroke-width: 2; stroke-linecap: round; }
.i1-panel-hdr-ttl { font-size: 12px; font-weight: 700; color: var(--i1-t1); flex: 1; }
.i1-collapse-btn { width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; border-radius: 6px; cursor: pointer; color: var(--i1-t3); transition: background .12s; }
.i1-collapse-btn:hover { background: var(--i1-brd-l); color: var(--i1-t1); }
.i1-collapse-btn svg { width: 14px; height: 14px; stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; }

.i1-sl-body {
  padding: 10px 14px 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.i1-fp-group { min-width: 140px; flex: 1; }
.i1-fp-full { flex-basis: 100%; }
.i1-fp-lbl { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: var(--i1-t3); margin-bottom: 7px; }
.i1-fp-opts { display: flex; flex-wrap: wrap; gap: 6px; }
.i1-fp-opt {
  padding: 5px 11px;
  border-radius: 20px;
  background: var(--i1-surf2);
  border: 1px solid var(--i1-brd);
  font-size: 11px;
  font-weight: 600;
  color: var(--i1-t2);
  cursor: pointer;
  transition: all .13s;
  white-space: nowrap;
}
.i1-fp-opt:hover { background: var(--i1-brd-l); color: var(--i1-t1); }
.i1-fp-opt.on { background: var(--i1-ac-glow); border-color: var(--i1-ac); color: var(--i1-ac); }
.i1-fp-search { position: relative; display: flex; align-items: center; }
.i1-fp-sico { position: absolute; left: 10px; width: 14px; height: 14px; stroke: var(--i1-t3); fill: none; stroke-width: 2; pointer-events: none; }
.i1-fp-sinput {
  width: 100%;
  background: var(--i1-surf2);
  border: 1px solid var(--i1-brd);
  border-radius: 8px;
  padding: 9px 36px 9px 32px;
  font-size: 13px;
  color: var(--i1-t1);
  font-family: inherit;
  outline: none;
  transition: border-color .13s;
}
.i1-fp-sinput::placeholder { color: var(--i1-t3); }
.i1-fp-sinput:focus { border-color: var(--i1-ac); }
.i1-fp-sclear { position: absolute; right: 8px; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--i1-t3); border-radius: 50%; transition: background .12s; }
.i1-fp-sclear:hover { background: var(--i1-brd-l); }
.i1-fp-sclear svg { width: 12px; height: 12px; stroke: currentColor; fill: none; stroke-width: 2.5; stroke-linecap: round; }
.i1-date-range-row { display: flex; align-items: center; gap: 8px; }
.i1-date-input { background: var(--i1-surf2); border: 1px solid var(--i1-brd); border-radius: 7px; padding: 7px 10px; font-size: 12px; color: var(--i1-t1); font-family: inherit; outline: none; flex: 1; }
.i1-date-input:focus { border-color: var(--i1-ac); }
.i1-date-sep { color: var(--i1-t3); font-size: 12px; }
.i1-collapse-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px;
  cursor: pointer;
  color: var(--i1-t3);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .4px;
  border-top: 1px solid var(--i1-brd);
  transition: background .12s, color .12s;
}
.i1-collapse-bar:hover { background: var(--i1-muted); color: var(--i1-t2); }
.i1-collapse-bar svg { width: 12px; height: 12px; stroke: currentColor; fill: none; stroke-width: 2.5; stroke-linecap: round; }

/* Charts panel */
.i1-charts-body { flex-direction: column; gap: 16px; }
.i1-chart-block { background: var(--i1-surf2); border-radius: 10px; border: 1px solid var(--i1-brd); padding: 12px 14px; }
.i1-chart-ttl { font-size: 11px; font-weight: 700; color: var(--i1-t2); margin-bottom: 10px; text-transform: uppercase; letter-spacing: .4px; }
.i1-cbar-list { display: flex; flex-direction: column; gap: 7px; }
.i1-cbar-row { display: flex; align-items: center; gap: 8px; }
.i1-cbar-name { font-size: 11px; color: var(--i1-t2); width: 80px; flex-shrink: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.i1-cbar-track { flex: 1; height: 6px; background: var(--i1-brd); border-radius: 3px; overflow: hidden; }
.i1-cbar-fill { height: 100%; border-radius: 3px; transition: width .4s ease; }
.i1-cbar-val { font-size: 11px; font-weight: 700; color: var(--i1-t2); width: 36px; text-align: right; }
.i1-sparkline-wrap { padding: 4px 0; }
.i1-sparkline { width: 100%; height: 60px; }

/* Viki panel */
.i1-viki-panel .i1-viki-messages { padding: 10px 14px; display: flex; flex-direction: column; gap: 8px; }
.i1-viki-empty { display: flex; align-items: center; gap: 8px; color: var(--i1-t3); font-size: 12px; padding: 12px 0; }
.i1-viki-empty svg { width: 18px; height: 18px; stroke: currentColor; fill: none; stroke-width: 2; flex-shrink: 0; }
.i1-viki-msg {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: var(--i1-surf2);
  border: 1px solid var(--i1-brd);
  border-radius: 10px;
  padding: 11px 13px;
}
.i1-viki-exam { border-color: rgba(99,102,241,.4); background: var(--i1-ac-glow); }
.i1-viki-congrats { border-color: rgba(34,197,94,.3); background: rgba(34,197,94,.07); }
.i1-viki-reminder { border-color: rgba(245,158,11,.3); background: rgba(245,158,11,.06); }
.i1-viki-msg-icon { width: 30px; height: 30px; border-radius: 50%; background: var(--i1-brd-l); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.i1-viki-msg-icon svg { width: 14px; height: 14px; stroke: var(--i1-ac); fill: none; stroke-width: 2; }
.i1-viki-exam .i1-viki-msg-icon svg { stroke: var(--i1-ac); }
.i1-viki-congrats .i1-viki-msg-icon svg { stroke: var(--i1-green); }
.i1-viki-reminder .i1-viki-msg-icon svg { stroke: var(--i1-yellow); }
.i1-viki-msg-body { flex: 1; }
.i1-viki-msg-text { font-size: 12px; color: var(--i1-t1); line-height: 1.5; }
.i1-viki-msg-action {
  margin-top: 8px;
  display: inline-block;
  padding: 5px 12px;
  background: var(--i1-ac);
  color: #fff;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity .13s;
}
.i1-viki-msg-action:hover { opacity: .85; }

/* Profile panel */
.i1-prof-panel {
  position: fixed;
  top: 0;
  right: auto;
  left: 0;
  border-left: none;
  width: 260px;
  height: 100vh;
  background: var(--i1-surface);
  border-right: 1px solid var(--i1-brd);
  z-index: 400;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  transform: translateX(-100%);
  transition: transform .25s cubic-bezier(.4,0,.2,1), box-shadow .25s;
}
.i1-prof-panel.c1-panel--open {
  transform: translateX(0);
  box-shadow: 4px 0 24px rgba(0,0,0,.12);
}
.i1-prof-head { padding: 16px 14px 10px; border-bottom: 1px solid var(--i1-brd); }
.i1-prof-brand-row { display: flex; align-items: center; margin-bottom: 12px; }
.i1-prof-brand-name { font-size: 16px; font-weight: 800; color: var(--i1-t1); letter-spacing: -.4px; }
.i1-prof-brand-name span { color: var(--i1-ac); }
.i1-prof-user-row { display: flex; align-items: center; gap: 10px; }
.i1-prof-av { width: 36px; height: 36px; border-radius: 50%; background: var(--i1-ac); display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; color: #fff; flex-shrink: 0; }
.i1-prof-name { font-size: 13px; font-weight: 700; color: var(--i1-t1); }
.i1-prof-role { font-size: 10px; color: var(--i1-t3); font-weight: 600; text-transform: uppercase; letter-spacing: .4px; }
.i1-prof-scroll { flex: 1; padding: 10px 0; }
.i1-nc-lbl { font-size: 9px; font-weight: 800; text-transform: uppercase; letter-spacing: .8px; color: var(--i1-t3); padding: 8px 14px 4px; }
.i1-ng { display: flex; flex-direction: column; gap: 1px; padding: 0 6px; }
.i1-nb {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--i1-t2);
  text-decoration: none;
  transition: background .13s, color .13s;
  cursor: pointer;
}
.i1-nb svg { width: 15px; height: 15px; stroke: currentColor; fill: none; stroke-width: 2; flex-shrink: 0; }
.i1-nb:hover { background: var(--i1-muted); color: var(--i1-t1); }
.i1-nb-active { background: var(--i1-ac-glow); color: var(--i1-ac); }
.i1-nb-logout { color: var(--i1-red); }
.i1-nb-logout:hover { background: rgba(199,84,80,.08); }

/* ── BODY ──────────────────────────────────────────────────────────────────── */
.i1-body { flex: 1; display: flex; flex-direction: column; padding-bottom: 80px; }

/* ── EXAM BANNER ───────────────────────────────────────────────────────────── */
.i1-exam-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 12px 14px 0;
  padding: 13px 16px;
  background: linear-gradient(135deg, rgba(124,92,252,.08), rgba(124,92,252,.05));
  border: 1px solid rgba(124,92,252,.3);
  border-radius: 12px;
  cursor: pointer;
  transition: opacity .14s;
}
.i1-exam-banner:hover { opacity: .88; }
.i1-exam-banner svg { width: 22px; height: 22px; stroke: var(--i1-ac); fill: none; stroke-width: 2; flex-shrink: 0; }
.i1-exam-banner-text { flex: 1; }
.i1-exam-banner-text strong { display: block; font-size: 13px; font-weight: 700; color: var(--i1-t1); }
.i1-exam-banner-text span { font-size: 11px; color: var(--i1-t2); }
.i1-exam-banner-cta { padding: 6px 14px; background: var(--i1-ac); color: #fff; border-radius: 8px; font-size: 11px; font-weight: 700; white-space: nowrap; }

/* ── SECTION HEADERS ───────────────────────────────────────────────────────── */
.i1-section-hdr {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 20px 16px 10px;
  font-size: 10.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .9px;
  color: var(--i1-t3);
}
.i1-section-hdr svg { width: 13px; height: 13px; stroke: var(--i1-ac); fill: none; stroke-width: 2; flex-shrink: 0; }
.i1-section-count { margin-left: 2px; padding: 2px 7px; background: rgba(124,92,252,.08); border: 1px solid rgba(124,92,252,.2); border-radius: 10px; font-size: 10px; color: var(--i1-ac); font-weight: 700; }
.i1-section-add-btn { margin-left: auto; display: flex; align-items: center; gap: 5px; padding: 5px 12px 5px 8px; background: linear-gradient(135deg, var(--i1-ac), var(--i1-ac2)); color: #fff; border: none; border-radius: 20px; font-size: 11px; font-weight: 700; cursor: pointer; transition: opacity .13s, box-shadow .13s; box-shadow: 0 2px 8px rgba(124,92,252,.3); letter-spacing: .1px; }
.i1-section-add-btn:hover { opacity: .9; box-shadow: 0 4px 14px rgba(124,92,252,.45); }
.i1-section-add-btn svg { width: 11px; height: 11px; stroke: #fff; fill: none; stroke-width: 3; stroke-linecap: round; flex-shrink: 0; }

/* ── ANALOGY CHIPS ─────────────────────────────────────────────────────────── */
.i1-analogies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 6px;
  padding: 0 14px 14px;
}
.i1-analogy-chip {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 12px;
  background: #fff;
  border: 1.5px solid var(--i1-brd);
  border-radius: 30px;
  cursor: pointer;
  transition: background .13s, border-color .13s, color .13s, box-shadow .13s, transform .1s;
  color: var(--i1-t1);
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0,0,0,.05);
}
.i1-analogy-chip:hover { background: rgba(124,92,252,.05); border-color: rgba(124,92,252,.4); color: var(--i1-ac); box-shadow: 0 2px 8px rgba(124,92,252,.12); transform: translateY(-1px); }
.i1-analogy-chip.active { background: linear-gradient(135deg, rgba(124,92,252,.1), rgba(155,125,253,.08)); border-color: var(--i1-ac); color: var(--i1-ac); box-shadow: 0 2px 10px rgba(124,92,252,.18); }
.i1-analogy-chip-icon { font-size: 15px; flex-shrink: 0; line-height: 1; }
.i1-analogy-chip-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 11.5px; letter-spacing: -.1px; }
.i1-analogy-chip-count { font-size: 9px; font-weight: 700; color: var(--i1-ac); background: rgba(124,92,252,.1); border-radius: 10px; padding: 1px 5px; flex-shrink: 0; }

/* Inline analogy card (replaces modal) */
.i1-analogy-card {
  margin: 0 14px 14px;
  background: var(--i1-surface);
  border: 1.5px solid var(--i1-ac);
  border-radius: 12px;
  padding: 14px 16px;
}
.i1-analogy-card-hdr {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.i1-analogy-card-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  color: var(--i1-ac);
  background: rgba(124,92,252,.1);
  padding: 3px 10px;
  border-radius: 20px;
}
.i1-analogy-card-close {
  width: 26px; height: 26px; display: flex; align-items: center; justify-content: center;
  border: none; background: transparent; cursor: pointer; border-radius: 6px;
  color: var(--i1-t3); transition: background .12s, color .12s; flex-shrink: 0;
}
.i1-analogy-card-close:hover { background: var(--i1-brd-l); color: var(--i1-t1); }
.i1-analogy-card-close svg { width: 14px; height: 14px; stroke: currentColor; fill: none; stroke-width: 2.5; stroke-linecap: round; }
.i1-analogy-card-text {
  font-size: 13.5px;
  line-height: 1.65;
  color: var(--i1-t1);
  font-style: italic;
  padding: 10px 14px;
  background: var(--i1-surf2);
  border-left: 3px solid var(--i1-ac);
  border-radius: 0 8px 8px 0;
  margin-bottom: 12px;
}
.i1-analogy-card-foot {
  display: flex;
  align-items: center;
  gap: 10px;
}
/* When analogy card is inline inside the grid, override margin and span full width */
.i1-analogy-card.i1-analogy-card-inline { grid-column: 1 / -1; margin: 0 0 2px; }
.i1-acc-fade-enter-active, .i1-acc-fade-leave-active { transition: opacity .2s, transform .2s; }
.i1-acc-fade-enter, .i1-acc-fade-leave-to { opacity: 0; transform: translateY(-6px); }

/* ── COURSE LIST ───────────────────────────────────────────────────────────── */
.i1-course-list { display: flex; flex-direction: column; gap: 8px; padding: 0 14px 20px; }
.i1-course-row {
  background: #fff;
  border: 1.5px solid var(--i1-brd);
  border-radius: 14px;
  overflow: hidden;
  transition: border-color .15s, box-shadow .15s;
  box-shadow: 0 1px 4px rgba(0,0,0,.05);
}
.i1-course-row:hover { box-shadow: 0 3px 12px rgba(0,0,0,.08); }
.i1-course-row.expanded { border-color: var(--i1-ac); box-shadow: 0 2px 10px rgba(124,92,252,.12); }
.i1-course-head {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 14px 16px;
  cursor: pointer;
  transition: background .13s;
}
.i1-course-head:hover { background: rgba(124,92,252,.03); }
.i1-course-num {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--i1-ac) 0%, var(--i1-ac2) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
  color: #fff;
  flex-shrink: 0;
  letter-spacing: -.5px;
  box-shadow: 0 2px 6px rgba(124,92,252,.35);
}
.i1-course-info { flex: 1; min-width: 0; }
.i1-course-title { font-size: 14px; font-weight: 700; color: var(--i1-t1); margin-bottom: 7px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; letter-spacing: -.2px; }
.i1-course-progress-row { display: flex; align-items: center; gap: 8px; }
.i1-course-prog-track { flex: 1; height: 5px; background: var(--i1-surf2); border-radius: 10px; overflow: hidden; }
.i1-course-prog-fill { height: 100%; border-radius: 10px; background: linear-gradient(90deg, var(--i1-ac) 0%, var(--i1-ac2) 100%); transition: width .4s ease; }
.i1-course-prog-pct { font-size: 10px; color: var(--i1-t3); font-weight: 700; flex-shrink: 0; min-width: 28px; text-align: right; }
.i1-course-status-chip {
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 10.5px;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
  letter-spacing: .2px;
}
.i1-course-status-chip.status-not-started { background: #f5f5f5; color: var(--i1-t3); border: 1px solid var(--i1-brd); }
.i1-course-status-chip.status-in-progress { background: rgba(245,158,11,.1); color: #b07800; border: 1px solid rgba(245,158,11,.25); }
.i1-course-status-chip.status-completed    { background: rgba(45,157,94,.1); color: #1e7a4a; border: 1px solid rgba(45,157,94,.25); }
.i1-course-status-chip.status-exam-due     { background: rgba(232,137,28,.1); color: #b05c00; border: 1px solid rgba(232,137,28,.25); }
.i1-course-chv { color: var(--i1-t3); flex-shrink: 0; transition: transform .2s, color .15s; }
.i1-course-chv svg { width: 16px; height: 16px; stroke: currentColor; fill: none; stroke-width: 2.5; }
.i1-course-row.expanded .i1-course-chv { color: var(--i1-ac); }
.i1-course-chv.open { transform: rotate(180deg); }

/* Course expanded body */
.i1-course-expanded { border-top: 1.5px solid var(--i1-brd-l); padding: 14px 16px 16px; background: #fafafa; }
.i1-course-desc { font-size: 12.5px; color: var(--i1-t2); line-height: 1.65; margin-bottom: 14px; padding: 10px 12px; background: #fff; border-radius: 8px; border: 1px solid var(--i1-brd-l); }

/* Lessons list */
.i1-lessons-list { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
.i1-lesson-acc { display: flex; flex-direction: column; }
.i1-lesson-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--i1-surf2);
  border: 1.5px solid transparent;
  cursor: pointer;
  transition: all .15s;
  user-select: none;
}
.i1-lesson-row:hover { border-color: var(--i1-brd); background: var(--i1-brd-l); }
.i1-lesson-row.read { border-color: rgba(45,157,94,.25); background: rgba(45,157,94,.06); }
.i1-lesson-row.open { border-radius: 10px 10px 0 0; border-color: var(--i1-ac); background: rgba(124,92,252,.05); border-bottom-color: transparent; }
.i1-lesson-row.read.open { border-color: rgba(45,157,94,.4); border-bottom-color: transparent; background: rgba(45,157,94,.08); }
.i1-lesson-status-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--i1-brd);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  color: var(--i1-t3);
  transition: background .15s;
}
.i1-lesson-status-dot.read { background: var(--i1-green); color: #fff; }
.i1-lesson-status-dot svg { width: 11px; height: 11px; stroke: #fff; fill: none; stroke-width: 3; stroke-linecap: round; stroke-linejoin: round; }
.i1-lesson-title { flex: 1; font-size: 12.5px; font-weight: 600; color: var(--i1-t1); line-height: 1.3; }
.i1-lesson-chv { width: 14px; height: 14px; stroke: var(--i1-t3); fill: none; stroke-width: 2; flex-shrink: 0; transition: transform .2s, stroke .15s; }
.i1-lesson-row.open .i1-lesson-chv { transform: rotate(180deg); stroke: var(--i1-ac); }

/* Inline lesson content body */
.i1-lesson-body {
  background: var(--i1-surface);
  border: 1.5px solid var(--i1-ac);
  border-top: none;
  border-radius: 0 0 10px 10px;
  padding: 16px 14px 14px;
}
.i1-lesson-row.read + .i1-lesson-body { border-color: rgba(45,157,94,.4); }
.i1-lesson-body-pos {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  color: var(--i1-t3);
  text-transform: uppercase;
  letter-spacing: .6px;
  margin-bottom: 12px;
  background: var(--i1-surf2);
  padding: 3px 8px;
  border-radius: 20px;
}
.i1-lessons-loading { display: flex; align-items: center; gap: 8px; font-size: 11px; color: var(--i1-t3); padding: 8px 6px; }

/* Course actions */
.i1-course-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.i1-course-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: opacity .13s;
}
.i1-course-btn:hover { opacity: .85; }
.i1-course-btn svg { width: 13px; height: 13px; stroke: currentColor; fill: currentColor; stroke-width: 0; }
.i1-course-btn.i1-course-btn-start    { background: var(--i1-ac); color: #fff; }
.i1-course-btn.i1-course-btn-continue { background: rgba(245,158,11,.2); color: var(--i1-yellow); }
.i1-course-btn.i1-course-btn-exam     { background: rgba(251,146,60,.2); color: var(--i1-orange); }
.i1-course-btn.i1-course-btn-exam svg { stroke: currentColor; fill: none; stroke-width: 2; }
.i1-course-btn.i1-course-btn-continue svg { fill: none; stroke: currentColor; stroke-width: 2; }
.i1-course-last-score { font-size: 11px; color: var(--i1-t3); margin-left: 4px; }
.i1-course-last-score .pass { color: var(--i1-green); }
.i1-course-last-score .fail { color: var(--i1-red); }

/* ── LESSON VIEWER CONTENT ─────────────────────────────────────────────────── */
.i1-lesson-progress-bar { height: 3px; background: var(--i1-brd); }
.i1-lesson-progress-fill { height: 100%; background: var(--i1-ac); transition: width .3s ease; }
.i1-lesson-video { margin-bottom: 16px; border-radius: 10px; overflow: hidden; aspect-ratio: 16/9; }
.i1-lesson-video iframe { width: 100%; height: 100%; }
.i1-lesson-images { display: flex; flex-direction: column; gap: 8px; margin-bottom: 14px; }
.i1-lesson-img { width: 100%; border-radius: 8px; }
.i1-lesson-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 24px; padding-top: 16px; border-top: 1px solid var(--i1-brd); }
.i1-lesson-read-btn {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px;
  border-radius: 9px;
  background: var(--i1-surf2);
  border: 1px solid var(--i1-brd);
  color: var(--i1-t2);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all .13s;
}
.i1-lesson-read-btn.done { background: rgba(34,197,94,.12); border-color: rgba(34,197,94,.3); color: var(--i1-green); }
.i1-lesson-read-btn svg { width: 15px; height: 15px; stroke: currentColor; fill: none; stroke-width: 2; }
.i1-lesson-next-btn, .i1-lesson-exam-btn {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: opacity .13s;
}
.i1-lesson-next-btn:hover, .i1-lesson-exam-btn:hover { opacity: .85; }
.i1-lesson-next-btn { background: var(--i1-ac); color: #fff; }
.i1-lesson-exam-btn { background: var(--i1-orange); color: #fff; }
.i1-lesson-next-btn svg, .i1-lesson-exam-btn svg { width: 14px; height: 14px; stroke: currentColor; fill: none; stroke-width: 2.5; }

/* ── DRAWERS ───────────────────────────────────────────────────────────────── */
.i1-drawer-wrap {
  position: fixed;
  inset: 0;
  z-index: 500;
  display: flex;
  justify-content: flex-end;
}
.i1-drawer-wrap-left { justify-content: flex-start; }
.i1-drawer {
  width: min(420px, 100vw);
  height: 100vh;
  background: var(--i1-surface);
  border-left: 1px solid var(--i1-brd);
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 40px rgba(0,0,0,.12);
  overflow: hidden;
}
.i1-drawer-left { border-left: none; border-right: 1px solid var(--i1-brd); box-shadow: 8px 0 40px rgba(0,0,0,.12); }
.i1-drawer-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 14px 12px;
  border-bottom: 1px solid var(--i1-brd);
  flex-shrink: 0;
}
.i1-drawer-back {
  width: 30px; height: 30px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  color: var(--i1-t2);
  flex-shrink: 0;
  transition: background .12s;
}
.i1-drawer-back:hover { background: var(--i1-brd-l); color: var(--i1-t1); }
.i1-drawer-back svg { width: 18px; height: 18px; stroke: currentColor; fill: none; stroke-width: 2.5; }
.i1-drawer-title-wrap { flex: 1; min-width: 0; }
.i1-drawer-title { font-size: 15px; font-weight: 700; color: var(--i1-t1); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.i1-drawer-sub { font-size: 11px; color: var(--i1-t3); margin-top: 2px; }
.i1-drawer-close {
  width: 30px; height: 30px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 8px; border: none; background: transparent;
  cursor: pointer; color: var(--i1-t3); flex-shrink: 0; transition: background .12s, color .12s;
}
.i1-drawer-close:hover { background: var(--i1-brd-l); color: var(--i1-t1); }
.i1-drawer-close svg { width: 16px; height: 16px; stroke: currentColor; fill: none; stroke-width: 2.5; stroke-linecap: round; }
.i1-drawer-body { flex: 1; overflow-y: auto; padding: 16px; }

/* Transitions */
.i1-drawer-enter-active, .i1-drawer-leave-active { transition: transform .28s cubic-bezier(.22,1,.36,1); }
.i1-drawer-enter, .i1-drawer-leave-to { transform: translateX(100%); }
.i1-drawer-left-enter-active, .i1-drawer-left-leave-active { transition: transform .28s cubic-bezier(.22,1,.36,1); }
.i1-drawer-left-enter, .i1-drawer-left-leave-to { transform: translateX(-100%); }

/* ── DOC CONTENT ───────────────────────────────────────────────────────────── */
.i1-doc-content { font-size: 13px; color: var(--i1-t2); line-height: 1.75; }
.i1-doc-content h2 { font-size: 16px; font-weight: 700; color: var(--i1-t1); margin: 0 0 10px; }
.i1-doc-content h3 { font-size: 13px; font-weight: 700; color: var(--i1-t1); margin: 14px 0 6px; }
.i1-doc-content p { margin: 0 0 10px; }
.i1-doc-content ul, .i1-doc-content ol { padding-left: 20px; margin: 0 0 10px; }
.i1-doc-content li { margin-bottom: 5px; }
.i1-doc-content strong { color: var(--i1-t1); }
.i1-doc-content em { color: var(--i1-ac2); font-style: normal; }

/* ── ADD COURSE FORM ───────────────────────────────────────────────────────── */
.i1-form-group { margin-bottom: 14px; }
.i1-form-lbl { display: block; font-size: 11px; font-weight: 700; color: var(--i1-t3); text-transform: uppercase; letter-spacing: .4px; margin-bottom: 6px; }
.i1-form-hint { font-weight: 500; text-transform: none; }
.i1-req { color: var(--i1-red); }
.i1-form-input {
  display: block; width: 100%;
  background: var(--i1-surf2);
  border: 1px solid var(--i1-brd);
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 13px;
  color: var(--i1-t1);
  font-family: inherit;
  outline: none;
  box-sizing: border-box;
  transition: border-color .13s;
}
.i1-form-input:focus { border-color: var(--i1-ac); }
.i1-form-input::placeholder { color: var(--i1-t3); }
.i1-form-textarea {
  display: block; width: 100%;
  background: var(--i1-surf2);
  border: 1px solid var(--i1-brd);
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 13px;
  color: var(--i1-t1);
  font-family: inherit;
  outline: none;
  resize: vertical;
  box-sizing: border-box;
  transition: border-color .13s;
}
.i1-form-textarea:focus { border-color: var(--i1-ac); }
.i1-form-section-hdr {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  font-weight: 800;
  color: var(--i1-t3);
  text-transform: uppercase;
  letter-spacing: .5px;
  padding: 10px 0 8px;
  border-top: 1px solid var(--i1-brd);
  margin-top: 4px;
}
.i1-form-add-btn {
  padding: 4px 10px;
  background: var(--i1-ac-glow);
  border: 1px solid var(--i1-ac);
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  color: var(--i1-ac);
  cursor: pointer;
  text-transform: none;
  letter-spacing: 0;
  transition: opacity .13s;
}
.i1-form-add-btn:hover { opacity: .8; }
.i1-form-lesson-block {
  background: var(--i1-surf2);
  border: 1px solid var(--i1-brd);
  border-radius: 9px;
  padding: 11px 12px;
  margin-bottom: 8px;
}
.i1-form-lesson-hdr { display: flex; justify-content: space-between; align-items: center; font-size: 11px; font-weight: 700; color: var(--i1-t2); margin-bottom: 8px; }
.i1-form-remove-btn { padding: 3px 8px; background: rgba(239,68,68,.12); border: 1px solid rgba(239,68,68,.3); border-radius: 5px; font-size: 10px; font-weight: 700; color: var(--i1-red); cursor: pointer; }
.i1-form-option-row { display: flex; align-items: center; gap: 7px; margin-bottom: 6px; }
.i1-form-correct-dot {
  width: 16px; height: 16px;
  border-radius: 50%;
  border: 2px solid var(--i1-brd);
  flex-shrink: 0;
  cursor: pointer;
  transition: all .13s;
}
.i1-form-correct-dot.active { background: var(--i1-green); border-color: var(--i1-green); }
.i1-form-correct-hint { font-size: 10px; color: var(--i1-t3); margin-top: 4px; }
.i1-form-actions { display: flex; gap: 8px; margin-top: 16px; }
.i1-submit-btn {
  flex: 1;
  padding: 10px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: opacity .13s;
}
.i1-submit-btn:disabled { opacity: .45; cursor: default; }
.i1-submit-btn:not(:disabled):hover { opacity: .85; }
.i1-submit-draft { background: var(--i1-surf2); color: var(--i1-t2); border: 1px solid var(--i1-brd); }
.i1-submit-publish { background: var(--i1-ac); color: #fff; }
.i1-submit-publish svg { width: 14px; height: 14px; stroke: currentColor; fill: none; stroke-width: 2; }

/* ── EXAM MODAL ────────────────────────────────────────────────────────────── */
.i1-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.i1-modal-fade-enter-active, .i1-modal-fade-leave-active { transition: opacity .2s; }
.i1-modal-fade-enter, .i1-modal-fade-leave-to { opacity: 0; }
.i1-modal {
  background: var(--i1-surface);
  border: 1px solid var(--i1-brd);
  border-radius: 16px;
  width: min(500px, 100%);
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 24px 80px rgba(0,0,0,.15);
}
.i1-modal-analogy { width: min(420px, 100%); }
.i1-modal-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 16px 12px;
  border-bottom: 1px solid var(--i1-brd);
}
.i1-modal-title { flex: 1; font-size: 14px; font-weight: 700; color: var(--i1-t1); display: flex; align-items: center; gap: 8px; }
.i1-modal-title svg { width: 16px; height: 16px; stroke: var(--i1-ac); fill: none; stroke-width: 2; flex-shrink: 0; }
.i1-modal-close { width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; border-radius: 8px; border: none; background: transparent; cursor: pointer; color: var(--i1-t3); transition: background .12s; }
.i1-modal-close:hover { background: var(--i1-brd-l); color: var(--i1-t1); }
.i1-modal-close svg { width: 14px; height: 14px; stroke: currentColor; fill: none; stroke-width: 2.5; stroke-linecap: round; }

/* Exam body */
.i1-exam-body { padding: 16px; }
.i1-exam-progress-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.i1-exam-qnum { font-size: 11px; font-weight: 700; color: var(--i1-t3); text-transform: uppercase; letter-spacing: .4px; }
.i1-exam-timer { display: flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 700; color: var(--i1-t2); }
.i1-exam-timer svg { width: 13px; height: 13px; stroke: currentColor; fill: none; stroke-width: 2; }
.i1-exam-timer.urgent { color: var(--i1-red); }
.i1-exam-progress-bar { height: 4px; background: var(--i1-brd); border-radius: 2px; margin-bottom: 18px; overflow: hidden; }
.i1-exam-progress-fill { height: 100%; background: var(--i1-ac); transition: width .3s ease; }
.i1-exam-question { font-size: 15px; font-weight: 600; color: var(--i1-t1); line-height: 1.55; margin-bottom: 18px; }
.i1-exam-options { display: flex; flex-direction: column; gap: 8px; margin-bottom: 20px; }
.i1-exam-opt {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: var(--i1-surf2);
  border: 1px solid var(--i1-brd);
  border-radius: 10px;
  font-size: 13px;
  color: var(--i1-t2);
  cursor: pointer;
  transition: all .13s;
}
.i1-exam-opt:hover { border-color: var(--i1-ac); color: var(--i1-t1); background: var(--i1-ac-glow); }
.i1-exam-opt.selected { border-color: var(--i1-ac); background: var(--i1-ac-glow); color: var(--i1-t1); }
.i1-exam-opt.correct { border-color: var(--i1-green); background: rgba(34,197,94,.12); color: var(--i1-green); }
.i1-exam-opt.wrong { border-color: var(--i1-red); background: rgba(239,68,68,.1); color: var(--i1-red); }
.i1-exam-opt-letter {
  width: 26px; height: 26px;
  border-radius: 6px;
  background: var(--i1-brd);
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 800;
  flex-shrink: 0;
  color: var(--i1-t3);
}
.i1-exam-opt.selected .i1-exam-opt-letter,
.i1-exam-opt.correct .i1-exam-opt-letter { background: var(--i1-ac); color: #fff; }
.i1-exam-opt.wrong .i1-exam-opt-letter { background: var(--i1-red); color: #fff; }
.i1-exam-nav { display: flex; justify-content: flex-end; }
.i1-exam-next-btn {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px 20px;
  background: var(--i1-ac);
  color: #fff;
  border: none;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity .13s;
}
.i1-exam-next-btn:disabled { opacity: .4; cursor: default; }
.i1-exam-next-btn:not(:disabled):hover { opacity: .85; }
.i1-exam-next-btn svg { width: 14px; height: 14px; stroke: currentColor; fill: none; stroke-width: 2.5; }

/* Exam result */
.i1-exam-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 20px;
  gap: 10px;
}
.i1-exam-result-icon {
  width: 64px; height: 64px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 8px;
}
.i1-exam-result-icon.pass { background: rgba(34,197,94,.2); }
.i1-exam-result-icon.fail { background: rgba(239,68,68,.15); }
.i1-exam-result-icon svg { width: 28px; height: 28px; fill: none; stroke-width: 3; stroke-linecap: round; stroke-linejoin: round; }
.i1-exam-result-icon.pass svg { stroke: var(--i1-green); }
.i1-exam-result-icon.fail svg { stroke: var(--i1-red); }
.i1-exam-result-score { font-size: 36px; font-weight: 800; color: var(--i1-t1); }
.i1-exam-result-pct { font-size: 14px; color: var(--i1-t2); }
.i1-exam-result-msg { font-size: 16px; font-weight: 800; padding: 8px 20px; border-radius: 10px; }
.i1-exam-result-msg.pass { color: var(--i1-green); background: rgba(34,197,94,.12); }
.i1-exam-result-msg.fail { color: var(--i1-red); background: rgba(239,68,68,.1); }
.i1-exam-done-btn { margin-top: 8px; padding: 10px 28px; background: var(--i1-ac); color: #fff; border: none; border-radius: 9px; font-size: 13px; font-weight: 700; cursor: pointer; transition: opacity .13s; }
.i1-exam-done-btn:hover { opacity: .85; }

/* ── ANALOGY MODAL ─────────────────────────────────────────────────────────── */
.i1-analogy-body { padding: 18px 18px 20px; }
.i1-analogy-profession-badge { display: inline-block; padding: 4px 12px; background: var(--i1-ac-glow); border: 1px solid var(--i1-ac); border-radius: 20px; font-size: 11px; font-weight: 700; color: var(--i1-ac); margin-bottom: 14px; }
.i1-analogy-text { font-size: 14px; color: var(--i1-t1); line-height: 1.7; border-left: 3px solid var(--i1-ac); padding-left: 14px; margin-bottom: 18px; font-style: italic; }
.i1-analogy-actions { display: flex; align-items: center; gap: 10px; }
.i1-analogy-refresh-btn {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 16px;
  background: var(--i1-surf2);
  border: 1px solid var(--i1-brd);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  color: var(--i1-t2);
  cursor: pointer;
  transition: all .13s;
}
.i1-analogy-refresh-btn:hover { border-color: var(--i1-ac); color: var(--i1-ac); }
.i1-analogy-refresh-btn svg { width: 13px; height: 13px; stroke: currentColor; fill: none; stroke-width: 2; }
.i1-analogy-count { font-size: 11px; color: var(--i1-t3); }

/* ── MANAGER VIEW ──────────────────────────────────────────────────────────── */
.i1-mgr-sort-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 14px 8px;
}
.i1-mgr-sort-lbl { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: var(--i1-t3); margin-right: 2px; }
.i1-mgr-sort-btn {
  padding: 4px 10px;
  border-radius: 14px;
  font-size: 10px;
  font-weight: 700;
  color: var(--i1-t2);
  background: var(--i1-surf2);
  border: 1px solid var(--i1-brd);
  cursor: pointer;
  transition: all .13s;
}
.i1-mgr-sort-btn.on { background: var(--i1-ac-glow); border-color: var(--i1-ac); color: var(--i1-ac); }
.i1-mgr-table-wrap { padding: 0 14px 14px; overflow-x: auto; }
.i1-mgr-table { width: 100%; border-collapse: collapse; }
.i1-mgr-table th {
  text-align: left;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .5px;
  color: var(--i1-t3);
  padding: 8px 10px;
  border-bottom: 1px solid var(--i1-brd);
}
.i1-mgr-row { cursor: pointer; border-bottom: 1px solid var(--i1-brd); transition: background .12s; }
.i1-mgr-row:hover { background: var(--i1-muted); }
.i1-mgr-row td { padding: 11px 10px; font-size: 12px; color: var(--i1-t2); vertical-align: middle; }
.i1-mgr-row-alert td { color: var(--i1-red); }
.i1-mgr-row-alert:hover { background: rgba(239,68,68,.06); }
.i1-mgr-agent-cell { display: flex; align-items: center; gap: 9px; }
.i1-mgr-av { width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; color: #fff; flex-shrink: 0; }
.i1-mgr-inactive-badge { padding: 2px 7px; background: rgba(239,68,68,.12); border-radius: 8px; font-size: 9px; font-weight: 700; color: var(--i1-red); }
.i1-score-pass { color: var(--i1-green); font-weight: 700; }
.i1-score-fail { color: var(--i1-red); font-weight: 700; }
.i1-status-pill { padding: 3px 9px; border-radius: 9px; font-size: 10px; font-weight: 700; }
.i1-status-pill.status-active     { background: rgba(34,197,94,.12); color: var(--i1-green); }
.i1-status-pill.status-struggling { background: rgba(239,68,68,.1);  color: var(--i1-red); }
.i1-status-pill.status-not-started { background: rgba(160,158,152,.12); color: var(--i1-t3); }

/* Agent detail drawer */
.i1-agent-summary-row { display: flex; gap: 10px; margin-bottom: 18px; }
.i1-agent-stat-card { flex: 1; background: var(--i1-surf2); border: 1px solid var(--i1-brd); border-radius: 10px; padding: 12px 10px; text-align: center; }
.i1-agent-stat-val { font-size: 20px; font-weight: 800; color: var(--i1-t1); }
.i1-agent-stat-val.pass { color: var(--i1-green); }
.i1-agent-stat-val.fail { color: var(--i1-red); }
.i1-agent-stat-lbl { font-size: 10px; color: var(--i1-t3); font-weight: 700; text-transform: uppercase; letter-spacing: .4px; margin-top: 4px; }
.i1-agent-history-ttl { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: .5px; color: var(--i1-t3); margin-bottom: 10px; }
.i1-agent-history-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid var(--i1-brd);
  font-size: 12px;
}
.i1-agent-history-name { flex: 1; color: var(--i1-t1); font-weight: 600; }
.i1-agent-history-score { font-weight: 700; width: 60px; text-align: center; }
.i1-agent-history-score.pass { color: var(--i1-green); }
.i1-agent-history-score.fail { color: var(--i1-red); }
.i1-agent-history-score.none { color: var(--i1-t3); }
.i1-agent-history-status { padding: 3px 8px; border-radius: 8px; font-size: 10px; font-weight: 700; }
.i1-agent-history-status.status-completed  { background: rgba(34,197,94,.12); color: var(--i1-green); }
.i1-agent-history-status.status-in-progress { background: rgba(245,158,11,.12); color: var(--i1-yellow); }
.i1-agent-history-status.status-not-started { background: rgba(160,158,152,.12); color: var(--i1-t3); }
.i1-agent-history-status.status-exam-due   { background: rgba(251,146,60,.12); color: var(--i1-orange); }
.i1-agent-history-date { font-size: 11px; color: var(--i1-t3); width: 70px; text-align: right; }

/* ── LOADING / EMPTY ───────────────────────────────────────────────────────── */
.i1-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; padding: 60px 20px; }
.i1-loading-txt { font-size: 12px; color: var(--i1-t3); }
.i1-spinner {
  width: 28px; height: 28px;
  border: 2px solid var(--i1-brd);
  border-top-color: var(--i1-ac);
  border-radius: 50%;
  animation: i1-spin .7s linear infinite;
}
.i1-spinner-sm { width: 14px; height: 14px; }
@keyframes i1-spin { to { transform: rotate(360deg); } }
.i1-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; padding: 60px 20px; }
.i1-empty-icon svg { width: 36px; height: 36px; stroke: var(--i1-t3); fill: none; stroke-width: 1.5; }
.i1-empty-msg { font-size: 14px; font-weight: 600; color: var(--i1-t2); }

/* ── FAB ───────────────────────────────────────────────────────────────────── */
.i1-fab {
  position: fixed;
  bottom: 22px; right: 18px;
  width: 50px; height: 50px;
  border-radius: 50%;
  background: var(--i1-ac);
  border: none;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 20px rgba(99,102,241,.45);
  z-index: 300;
  transition: transform .15s, box-shadow .15s;
}
.i1-fab:hover { transform: scale(1.08); box-shadow: 0 6px 28px rgba(99,102,241,.6); }
.i1-fab svg { width: 22px; height: 22px; stroke: #fff; fill: none; stroke-width: 2.5; stroke-linecap: round; }

/* ─── SETTINGS ─────────────────────────────────────────────────────────────── */
.i1-settings-wrap { display: flex; flex-direction: column; padding: 20px; gap: 0; max-width: 820px; overflow-y: auto; }
.i1-set-section {
  padding: 0 0 20px;
  border-bottom: 1px solid var(--i1-brd);
  margin-bottom: 20px;
}
.i1-set-section:last-of-type { border-bottom: none; margin-bottom: 0; }
.i1-set-section-title {
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; font-weight: 700; color: var(--i1-t1);
  margin-bottom: 14px;
}
.i1-set-section-title svg { width: 15px; height: 15px; stroke: var(--i1-ac); fill: none; stroke-width: 2; }
.i1-set-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0; }
.i1-set-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 0; border-bottom: 1px solid var(--i1-brd); gap: 16px;
}
.i1-set-row:last-child { border-bottom: none; }
.i1-set-row.i1-set-row-col { grid-column: 1 / -1; flex-direction: column; align-items: flex-start; gap: 10px; }
.i1-set-lbl { display: flex; flex-direction: column; gap: 2px; }
.i1-set-lbl-name { font-size: 13px; font-weight: 600; color: var(--i1-t1); }
.i1-set-lbl-hint { font-size: 11px; color: var(--i1-t3, #94a3b8); }
.i1-set-ctrl { display: flex; align-items: center; flex-shrink: 0; }
.i1-set-num-wrap {
  display: flex; align-items: center;
  border: 1.5px solid var(--i1-brd); border-radius: 8px; overflow: hidden;
  background: var(--i1-bg);
}
.i1-set-num {
  width: 58px; padding: 7px 10px; border: none; outline: none;
  background: transparent; color: var(--i1-t1); font-size: 14px; font-weight: 600; text-align: center;
}
.i1-set-unit {
  padding: 7px 10px; background: var(--i1-surf); color: var(--i1-t3, #94a3b8);
  font-size: 12px; font-weight: 600; border-left: 1.5px solid var(--i1-brd);
}
.i1-set-toggle {
  width: 42px; height: 24px; border-radius: 12px; background: var(--i1-brd);
  position: relative; cursor: pointer; transition: background .2s;
}
.i1-set-toggle.on { background: var(--i1-ac); }
.i1-set-toggle-knob {
  position: absolute; top: 3px; left: 3px;
  width: 18px; height: 18px; border-radius: 50%; background: #fff;
  transition: left .2s; box-shadow: 0 1px 3px rgba(0,0,0,.2);
}
.i1-set-toggle.on .i1-set-toggle-knob { left: 21px; }
.i1-set-textarea {
  width: 100%; padding: 10px 12px; border: 1.5px solid var(--i1-brd); border-radius: 8px;
  background: var(--i1-bg); color: var(--i1-t1); font-size: 13px; resize: vertical;
  outline: none; font-family: inherit; line-height: 1.5;
}
.i1-set-textarea:focus { border-color: var(--i1-ac); }
.i1-set-actions { display: flex; justify-content: flex-end; padding-top: 4px; }
.i1-btn-save {
  display: flex; align-items: center; gap: 8px;
  padding: 9px 20px; background: var(--i1-ac); color: #fff;
  border: none; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer;
  transition: opacity .15s;
}
.i1-btn-save:hover { opacity: .88; }
.i1-btn-save svg { width: 14px; height: 14px; stroke: #fff; fill: none; stroke-width: 2; }
.i1-spin-sm {
  width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,.35); border-top-color: #fff;
  animation: i1-spin .7s linear infinite;
}
@keyframes i1-spin { to { transform: rotate(360deg); } }

/* Hide sliding panel — replaced by inline top-strip */
.c1-instruction-page .c1-panel { display: none !important; }
</style>
